import React, { useState, useEffect, useRef } from "react";
import { Head } from "@inertiajs/react";
import { Card, CardContent } from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import {
  Search,
  Plus,
  MoreVertical,
  Trash2,
  ChevronLeft,
  ChevronRight,
  AlertTriangle,
  MessageSquare,
  ChevronDown,
  ChevronRightIcon,
  ArrowUp,
  ArrowDown,
  X,
  Check,
  CheckSquare,
  RefreshCw,
  Settings,
  Grid3X3,
  List,
  GripVertical,
} from "lucide-react";
import { DatePickerWithRange } from "@/components/date-range-picker";
import { format, isBefore, startOfToday } from "date-fns";
import { TaskFormModal } from "@/components/tasks/task-form-modal";
import { TaskDetailModal } from "@/components/tasks/task-detail-modal";
import { StackedAssigneeAvatars } from "@/components/tasks/stacked-assignee-avatars";
import { BulkEditModal } from "@/components/tasks/bulk-edit-modal";
import { BulkActionSheet } from "@/components/tasks/bulk-action-sheet";
import Checklist from "@/components/ui/Checklist";
import { ChecklistIndicator } from "@/components/ui/ChecklistIndicator";
import { toast } from "@/components/ui/use-toast";
import { useToast } from "@/hooks/use-toast";
import { cn } from "@/lib/utils";
import { useMobile } from "@/hooks/use-mobile";
import { FilterDropdown } from "@/components/filter-dropdown";
import { useTasks, TaskProvider } from "@/lib/contexts/task-context";
import { useUsers } from "@/lib/contexts/user-context";
import { useWorkflow } from "@/lib/contexts/workflow-context";
import { AuthenticatedLayout } from "@/components/layout/authenticated-layout";
import { router, usePage } from "@inertiajs/react";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandItem,
} from "@/components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { DndContext, closestCenter } from '@dnd-kit/core';
import { arrayMove, SortableContext, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { useSortableConfig } from '@/hooks/use-sortable-config';

// Add CSS for the pulsing animation and tooltip positioning
const pulseAnimation = `
@keyframes pulse {
  0% {
    box-shadow: 0 0 0 0 rgba(220, 38, 38, 0.4);
  }
  70% {
    box-shadow: 0 0 0 10px rgba(220, 38, 38, 0);
  }
  100% {
    box-shadow: 0 0 0 0 rgba(220, 38, 38, 0);
  }
}

.pulse {
  animation: pulse 2s infinite;
}

/* Tooltip positioning for assignee avatars */
.tooltip-left {
  left: 0;
  transform: none;
}

.tooltip-left .absolute {
  left: 1rem;
  transform: none;
}

.tooltip-center {
  left: 50%;
  transform: translateX(-50%);
}

.tooltip-center .absolute {
  left: 50%;
  transform: translateX(-50%);
}
`;

// Update the mock data to use numeric priorities
const INITIAL_TASKS = [];

// Helper to format date as 'YYYY-MM-DD HH:mm:ss'
function formatDateTime(dateString) {
  const date = new Date(dateString);
  if (isNaN(date)) return null;
  // Always set time to 23:59:59 for due dates (to match backend)
  return `${date.getFullYear()}-${String(date.getMonth()+1).padStart(2,'0')}-${String(date.getDate()).padStart(2,'0')} 23:59:59`;
}

// Helper to create a backend-compatible optimistic task
function createOptimisticTask(formData, currentUser, availableUsers = []) {
  const now = new Date().toISOString().slice(0, 19).replace('T', ' ');
  const due = formatDateTime(formData.due || new Date());
  
  // Create users array - always include current user, and add assigned user if different
  const users = [
    {
      id: currentUser.id,
      name: `${currentUser.firstName} ${currentUser.lastName}`,
      email: currentUser.email,
    }
  ];
  
  // Add assigned users
  if (formData.assignedUserIds && formData.assignedUserIds.length > 0) {
    formData.assignedUserIds.forEach(userId => {
      const assignedUser = availableUsers.find(u => u.id.toString() === userId.toString());
      if (assignedUser) {
        users.push({
          id: assignedUser.id,
          name: assignedUser.name || `${assignedUser.firstName || ''} ${assignedUser.lastName || ''}`.trim() || assignedUser.email,
          email: assignedUser.email,
        });
      }
    });
  }
  
  return {
    id: `TEMP-${Date.now()}`,
    created_at: now,
    updated_at: now,
    name: formData.name,
    description: formData.description,
    category: formData.category,
    priority: Number(formData.priority),
    due: due,
    status: 'New',
    completed_date: null,
    deferrable: Number(formData.deferrable),
    missable: Number(formData.missable),
    task_type_categories: formData.taskTypeCategories || [],
    defer_date: null,
    link: formData.link || '',
    link_title: formData.linkTitle || '',
    users: users,
    notes: [],
    subtasks: [],
    is_parent: formData.isParent,
    parent_id: formData.isParent ? null : formData.parentId || null,
    isParent: formData.isParent,
    is_shared: !!(formData.assignment?.shared),
    isOptimistic: true,
  };
}

// Helper to extract plain text from contenteditable HTML (convert mention pills to @Name, strip other HTML tags)
function getNotePlainText(html) {
  // Replace mention pills with @Name
  let text = html.replace(/<span[^>]*class=["']mention-pill["'][^>]*>(@[^<]+)<\/span>/g, '$1');
  // Remove all other HTML tags
  text = text.replace(/<[^>]+>/g, '');
  return text.trim();
}

export default function TasksPage({ tasks: backendTasks, date, task, users, categories, permissions, csrf_token, workflows, isTodayView = false, userRole, userDepartmentId, departments, currentFilters }) {
  // Extract tasks array from paginator if it's a paginator object, otherwise use as-is
  const tasksArray = backendTasks?.data ? backendTasks.data : (Array.isArray(backendTasks) ? backendTasks : []);
  
  return (
    <>
      <Head title={isTodayView ? "DME | Today's Tasks" : "DME | Tasks"} />
      <AuthenticatedLayout>
      <TaskProvider initialTasks={tasksArray}>
        <TasksContent 
          backendTasks={backendTasks} 
          task={task} 
          users={users} 
          categories={categories} 
          permissions={permissions || {}} 
          csrf_token={csrf_token}
          workflows={workflows || []}
          isTodayView={isTodayView}
          userRole={userRole}
          userDepartmentId={userDepartmentId}
          departments={departments || []}
          currentFilters={currentFilters}
        />
      </TaskProvider>
    </AuthenticatedLayout>
    </>
  )
}

// Sortable category row component
function SortableCategoryRow({ 
  category, 
  taskCount, 
  isExpanded, 
  onToggle,
  isLoading = false
}) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: category });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <TableRow
      ref={setNodeRef}
      style={style}
      data-category={category}
      className={cn(
        "dark:bg-muted/10 hover:bg-muted/40 dark:hover:bg-muted/20 transition-all duration-200",
        isDragging && "opacity-60 shadow-lg"
      )}
      onClick={() => onToggle(category)}
      {...attributes}
    >
      <TableCell colSpan={9} className="py-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            {/* Drag Handle */}
            <div
              {...listeners}
              className="cursor-grab active:cursor-grabbing p-1 hover:bg-muted/50 rounded transition-colors"
              onClick={(e) => e.stopPropagation()}
            >
              <GripVertical className="h-4 w-4 text-muted-foreground" />
            </div>
            
            <Button
              variant="ghost"
              size="icon"
              className="h-6 w-6"
              onClick={(e) => {
                e.stopPropagation();
                onToggle(category);
              }}
            >
              {isExpanded ? (
                <ChevronDown className="h-4 w-4" />
              ) : (
                <ChevronRightIcon className="h-4 w-4" />
              )}
            </Button>
            <div className="flex items-center gap-2">
              <h3 className="font-semibold text-sm">{category}</h3>
              {isLoading ? (
                <RefreshCw className="h-4 w-4 animate-spin text-muted-foreground" />
              ) : (
                <Badge variant="secondary" className="text-xs">
                  {taskCount} task{taskCount !== 1 ? 's' : ''}
                </Badge>
              )}
            </div>
          </div>
        </div>
      </TableCell>
    </TableRow>
  );
}

function CategoryPaginationControls({ 
  category, 
  pagination, 
  onPageChange 
}) {
  if (!pagination || pagination.total === 0) return null;
  
  const { page, perPage, total } = pagination;
  const totalPages = Math.ceil(total / perPage);
  const startItem = (page - 1) * perPage + 1;
  const endItem = Math.min(page * perPage, total);
  
  if (totalPages <= 1) return null;
  
  return (
    <div className="flex items-center justify-between px-4 py-2 border-t bg-background">
      <div className="text-sm text-muted-foreground">
        Showing {startItem} to {endItem} of {total} tasks
      </div>
      <div className="flex items-center space-x-2">
        <Button
          variant="outline"
          size="icon"
          onClick={() => onPageChange(category, page - 1)}
          disabled={page <= 1}
        >
          <ChevronLeft className="h-4 w-4" />
        </Button>
        {Array.from({ length: Math.min(totalPages, 10) })
          .map((_, index) => {
            // Show pages around current page
            const startPage = Math.max(1, page - 4);
            const pageNum = startPage + index;
            if (pageNum > totalPages) return null;
            return (
              <Button
                key={pageNum}
                variant={page === pageNum ? "default" : "outline"}
                size="sm"
                className="h-8 w-8"
                onClick={() => onPageChange(category, pageNum)}
              >
                {pageNum}
              </Button>
            );
          })
          .filter(Boolean)}
        {totalPages > 10 && (
          <span className="text-sm text-muted-foreground px-2">
            ... {totalPages}
          </span>
        )}
        <Button
          variant="outline"
          size="icon"
          onClick={() => onPageChange(category, page + 1)}
          disabled={page >= totalPages}
        >
          <ChevronRight className="h-4 w-4" />
        </Button>
      </div>
    </div>
  );
}

function TasksContent({ backendTasks, task, users, categories, permissions, csrf_token, workflows: workflowsFromProps = [], isTodayView = false, userRole, userDepartmentId, departments, currentFilters: serverFilters }) {
  const { tasks, setTasks, addTask, updateTask, deleteTask } = useTasks();
  const { currentUser } = useUsers();
  const { workflows: workflowsFromContext } = useWorkflow();
  const { toast } = useToast();
  const { props } = usePage();
  
  // Use workflows from props (filtered by backend) if available, otherwise fall back to context
  const workflows = workflowsFromProps.length > 0 ? workflowsFromProps : workflowsFromContext;
  
  // Filter users based on userRole and userDepartmentId
  const filteredUsers = React.useMemo(() => {
    if (userRole === 'admin') {
      return users || []
    } else if (userRole === 'manager' && userDepartmentId) {
      // Filter users to only those in the manager's department and child departments
      const getChildDepartmentIds = (parentId) => {
        const childIds = []
        const findChildren = (parentId) => {
          (departments || []).forEach(dept => {
            if (dept.parent_id === parentId) {
              childIds.push(dept.id)
              findChildren(dept.id)
            }
          })
        }
        findChildren(parentId)
        return childIds
      }
      
      const allowedDeptIds = [userDepartmentId, ...getChildDepartmentIds(userDepartmentId)]
      return (users || []).filter(user => allowedDeptIds.includes(user.department_id))
    }
    return users || []
  }, [users, userRole, userDepartmentId, departments])
  
  // Get paginator from props (backendTasks is the paginator object)
  const tasksPaginator = backendTasks?.data ? backendTasks : (props.tasks?.data ? props.tasks : null);
  
  // Sync filter state with server filters on mount/update
  useEffect(() => {
    if (serverFilters) {
      if (serverFilters.search !== undefined) setSearchQuery(serverFilters.search || '');
      if (serverFilters.status !== undefined) setStatusFilter(serverFilters.status || []);
      if (serverFilters.priority !== undefined) setPriorityFilter(serverFilters.priority || 'All');
      if (serverFilters.category !== undefined) setCategoryFilter(serverFilters.category || 'All');
      if (serverFilters.acknowledged !== undefined) setShowUnacknowledgedOnly(serverFilters.acknowledged === 'unacknowledged');
      if (serverFilters.date_from || serverFilters.date_to) {
        setDateRange({
          from: serverFilters.date_from ? new Date(serverFilters.date_from) : undefined,
          to: serverFilters.date_to ? new Date(serverFilters.date_to) : undefined,
        });
      }
      if (serverFilters.sort_column && serverFilters.sort_direction) {
        setSortCriteria([{ column: serverFilters.sort_column, direction: serverFilters.sort_direction }]);
      }
      if (serverFilters.per_page) setRowsPerPage(serverFilters.per_page);
      if (serverFilters.page) setPage(serverFilters.page - 1); // Convert to 0-based
    }
  }, [serverFilters]);

  // Handle ?action=new URL parameter to open task form from quick actions menu
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.get('action') === 'new' && permissions.can_create_tasks) {
      setSelectedTask(undefined);
      setIsFormOpen(true);
      // Clean up URL
      router.replace(window.location.pathname, { preserveState: true, preserveScroll: true });
    }
  }, []);
  
  // Sync page state with paginator (paginator uses 1-based, we use 0-based in state)
  useEffect(() => {
    if (tasksPaginator && tasksPaginator.current_page) {
      setPage(tasksPaginator.current_page - 1); // Convert to 0-based index for local state
      setRowsPerPage(tasksPaginator.per_page || rowsPerPage);
    }
  }, [tasksPaginator]);

  // Track if we've synced category view state to server
  const hasSyncedCategoryView = useRef(false);
  
  // Sync category view state from localStorage to server on initial load
  useEffect(() => {
    // Only run once on mount
    if (hasSyncedCategoryView.current) return;
    hasSyncedCategoryView.current = true;
    
    // Check if localStorage has category view enabled but server might not know
    const storageKey = isTodayView ? 'tasks-today-categorized-view' : 'tasks-all-categorized-view';
    try {
      const saved = localStorage.getItem(storageKey);
      const isCategoryViewInLocalStorage = saved ? JSON.parse(saved) : false;
      
      // If localStorage says category view is enabled, ensure server knows
      // Check if server already knows by checking if initialCategoryTasks exists or if currentFilters has the flag
      const serverKnows = props.initialCategoryTasks || props.currentFilters?.is_categorized_view;
      
      if (isCategoryViewInLocalStorage && !serverKnows) {
        // Make a request to sync the state with server
        const currentFilters = {
          search: searchQuery,
          status: statusFilter,
          priority: priorityFilter === 'All' ? '' : priorityFilter,
          category: categoryFilter === 'All' ? '' : categoryFilter,
          acknowledged: showUnacknowledgedOnly ? 'unacknowledged' : '',
          date_from: dateRange?.from ? format(dateRange.from, 'yyyy-MM-dd') : '',
          date_to: dateRange?.to ? format(dateRange.to, 'yyyy-MM-dd') : '',
          sort_column: sortCriteria.length > 0 ? sortCriteria[0].column : 'created_at',
          sort_direction: sortCriteria.length > 0 ? sortCriteria[0].direction : 'desc',
          per_page: rowsPerPage,
          is_categorized_view: true,
        };
        
        // Remove empty values
        Object.keys(currentFilters).forEach(key => {
          if (currentFilters[key] === '' || currentFilters[key] === null || currentFilters[key] === undefined) {
            delete currentFilters[key];
          }
        });
        
        if (Array.isArray(currentFilters.status) && currentFilters.status.length === 0) {
          delete currentFilters.status;
        }
        
        const filterRoute = isTodayView ? '/tasks/today/filters' : '/tasks/filters';
        router.post(filterRoute, {
          filters: currentFilters,
          page: 1,
        }, {
          preserveState: true,
          preserveScroll: true,
          only: ['tasks', 'currentFilters', 'categoryCounts', 'initialCategoryTasks'],
        });
      }
    } catch (error) {
      console.error('Error syncing category view state:', error);
    }
  }, []); // Only run once on mount

  // Handle initial category tasks from server-side pre-loading
  useEffect(() => {
    const initialCategoryTasks = props.initialCategoryTasks;
    
    if (initialCategoryTasks && typeof initialCategoryTasks === 'object') {
      const newCategoryTasks = {};
      const newCategoryPagination = {};
      
      // Populate category tasks and pagination from pre-loaded data
      Object.keys(initialCategoryTasks).forEach(category => {
        const categoryData = initialCategoryTasks[category];
        if (categoryData && categoryData.tasks) {
          newCategoryTasks[category] = categoryData.tasks;
          if (categoryData.pagination) {
            newCategoryPagination[category] = {
              page: categoryData.pagination.current_page || 1,
              perPage: categoryData.pagination.per_page || 10,
              total: categoryData.pagination.total || 0
            };
          }
        }
      });
      
      // Only update if we have data
      if (Object.keys(newCategoryTasks).length > 0) {
        setCategoryTasks(prev => ({
          ...prev,
          ...newCategoryTasks
        }));
        setCategoryPagination(prev => ({
          ...prev,
          ...newCategoryPagination
        }));
      }
    }
  }, [props.initialCategoryTasks]);

  // Handle category tasks and counts from backend
  useEffect(() => {
    const categoryTasksData = props.categoryTasks;
    const categoryCountsData = props.categoryCounts || {};
    
    if (categoryTasksData && categoryTasksData.category && categoryTasksData.tasks) {
      const category = categoryTasksData.category;
      const tasks = categoryTasksData.tasks.data || [];
      const paginator = categoryTasksData.tasks;
      
      // Update category tasks
      setCategoryTasks(prev => ({
        ...prev,
        [category]: tasks
      }));
      
      // Update category pagination state
      setCategoryPagination(prev => ({
        ...prev,
        [category]: {
          page: paginator.current_page || 1,
          perPage: paginator.per_page || 10,
          total: paginator.total || 0
        }
      }));
      
      // Remove from loading
      setLoadingCategories(prev => {
        const next = new Set(prev);
        next.delete(category);
        return next;
      });
    }
  }, [props.categoryTasks, props.categoryCounts]);

  // Auto-refresh state
  const [autoRefreshEnabled, setAutoRefreshEnabled] = useState(true);
  const [lastRefreshTime, setLastRefreshTime] = useState(new Date());
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [refreshInterval, setRefreshInterval] = useState(10 * 60 * 1000); // 10 minutes in milliseconds
  const [showIntervalPopup, setShowIntervalPopup] = useState(false);
  const [lastSelectedInterval, setLastSelectedInterval] = useState(10); // Remember last selected interval in minutes

  // Load auto-refresh settings from localStorage
  useEffect(() => {
    try {
      const savedSettings = localStorage.getItem('tasks-auto-refresh-settings');
      if (savedSettings) {
        const settings = JSON.parse(savedSettings);
        setAutoRefreshEnabled(settings.autoRefreshEnabled !== false); // Default to true
        setRefreshInterval(settings.refreshInterval || 10 * 60 * 1000);
        setLastSelectedInterval(settings.lastSelectedInterval || 10);
      }
    } catch (error) {
      console.error('Error loading auto-refresh settings:', error);
    }
  }, []);

  // Save auto-refresh settings to localStorage
  useEffect(() => {
    try {
      localStorage.setItem('tasks-auto-refresh-settings', JSON.stringify({
        autoRefreshEnabled,
        refreshInterval,
        lastSelectedInterval,
      }));
    } catch (error) {
      console.error('Error saving auto-refresh settings:', error);
    }
  }, [autoRefreshEnabled, refreshInterval, lastSelectedInterval]);

  // Default filter values
  const getDefaultFilters = () => ({
    searchQuery: "",
    statusFilter: ["To Do", "In Progress"],
    priorityFilter: "All",
    categoryFilter: "All",
    dateRange: { from: undefined, to: undefined },
    sortCriteria: [
      { column: "dueDate", direction: "asc" },
      { column: "priority", direction: "desc" }
    ],
    userClearedSorting: false,
    rowsPerPage: 10,
    page: 0,
    showSubtasks: true,
  });

  // Load saved filters from localStorage on component mount
  // Filters expire after 1 hour (3600000 ms)
  const loadSavedFilters = () => {
    const TTL_MS = 3600000; // 1 hour
    try {
      const savedFilters = localStorage.getItem('tasks-filters');
      if (savedFilters) {
        const parsed = JSON.parse(savedFilters);
        
        // Check if filters have expiration timestamp
        if (parsed && typeof parsed === 'object' && 'savedAt' in parsed) {
          const now = new Date().getTime();
          const savedAt = parsed.savedAt;
          
          // Check if expired (older than 1 hour)
          if (now - savedAt > TTL_MS) {
            // Expired - remove from storage and return defaults
            localStorage.removeItem('tasks-filters');
            return getDefaultFilters();
          }
          
          // Not expired - update timestamp to reset expiration timer (rolling expiration)
          const filters = parsed.filters || parsed;
          const updatedData = {
            filters: filters,
            savedAt: now, // Reset timestamp to current time
          };
          localStorage.setItem('tasks-filters', JSON.stringify(updatedData));
          
          // Convert date strings back to Date objects
          let dateRange = { from: undefined, to: undefined };
          if (filters.dateRange) {
            if (filters.dateRange.from) {
              dateRange.from = new Date(filters.dateRange.from);
              // Check if the date is valid
              if (isNaN(dateRange.from.getTime())) {
                dateRange.from = undefined;
              }
            }
            if (filters.dateRange.to) {
              dateRange.to = new Date(filters.dateRange.to);
              // Check if the date is valid
              if (isNaN(dateRange.to.getTime())) {
                dateRange.to = undefined;
              }
            }
          }
          
          // Handle multi-sort migration from old single sort
          let sortCriteria = [];
          if (filters.sortCriteria) {
            // New multi-sort format
            sortCriteria = filters.sortCriteria;
          } else if (filters.sortColumn && filters.sortDirection) {
            // Migrate from old single sort format
            sortCriteria = [{ column: filters.sortColumn, direction: filters.sortDirection }];
          } else {
            // Check if user has explicitly cleared sorting
            if (filters.userClearedSorting) {
              // User has manually cleared sorting - respect their choice
              sortCriteria = [];
            } else {
              // New user or no preference - use default sorting
              sortCriteria = [
                { column: "dueDate", direction: "asc" },
                { column: "priority", direction: "desc" }
              ];
            }
          }
          
          return {
            searchQuery: filters.searchQuery || "",
            statusFilter: filters.statusFilter || ["To Do", "In Progress"],
            priorityFilter: filters.priorityFilter || "All",
            categoryFilter: filters.categoryFilter || "All",
            dateRange: dateRange,
            sortCriteria: sortCriteria,
            userClearedSorting: filters.userClearedSorting || false,
            rowsPerPage: filters.rowsPerPage || 10,
            page: filters.page || 0,
            showSubtasks: filters.showSubtasks !== undefined ? filters.showSubtasks : true,
          };
        }
        
        // Legacy format (no timestamp) - treat as expired and remove
        localStorage.removeItem('tasks-filters');
      }
    } catch (error) {
      console.error('Error loading saved filters:', error);
    }
    
    // Return default values if no saved filters or error
    return getDefaultFilters();
  };

  // Save filters to localStorage with timestamp for expiration
  const saveFilters = (filters) => {
    try {
      // Create a copy of filters and convert Date objects to ISO strings
      const filtersToSave = { ...filters };
      if (filtersToSave.dateRange) {
        if (filtersToSave.dateRange.from instanceof Date) {
          filtersToSave.dateRange.from = filtersToSave.dateRange.from.toISOString();
        }
        if (filtersToSave.dateRange.to instanceof Date) {
          filtersToSave.dateRange.to = filtersToSave.dateRange.to.toISOString();
        }
      }
      // Store with timestamp for expiration check
      const dataToStore = {
        filters: filtersToSave,
        savedAt: new Date().getTime(),
      };
      localStorage.setItem('tasks-filters', JSON.stringify(dataToStore));
    } catch (error) {
      console.error('Error saving filters:', error);
    }
  };

  // Load initial filter values
  const initialFilters = loadSavedFilters();

  const [searchQuery, setSearchQuery] = useState(initialFilters.searchQuery);
  const [statusFilter, setStatusFilter] = useState(initialFilters.statusFilter);
  const [priorityFilter, setPriorityFilter] = useState(initialFilters.priorityFilter);
  const [categoryFilter, setCategoryFilter] = useState(initialFilters.categoryFilter);
  const [showUnacknowledgedOnly, setShowUnacknowledgedOnly] = useState(false);
  const [selectedTasks, setSelectedTasks] = useState([]);
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [isDetailOpen, setIsDetailOpen] = useState(false);
  const [isBulkEditOpen, setIsBulkEditOpen] = useState(false);
  const [selectedTask, setSelectedTask] = useState(undefined);
  const [detailTask, setDetailTask] = useState(undefined);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isAtBottom, setIsAtBottom] = useState(false);
  const [expandedTasks, setExpandedTasks] = useState([]);
  const scrollRef = useRef(false);

  // Helper function to check if all selected tasks are deferrable
  const checkCanDefer = () => {
    if (selectedTasks.length === 0) return false;
    const selectedTaskObjects = tasks.filter(task => selectedTasks.includes(task.id));
    const canDefer = selectedTaskObjects.every(task => task.deferrable == 1);
    return canDefer;
  };
  const [showSubtasks, setShowSubtasks] = useState(initialFilters.showSubtasks);
  const [today] = useState(startOfToday());

  // Replace single sort state with multi-sort array
  const [sortCriteria, setSortCriteria] = useState(initialFilters.sortCriteria);

  // Track the last clicked column to reset sort cycle
  const [lastClickedColumn, setLastClickedColumn] = useState(null);

  // Track if user has manually cleared sorting
  const [userClearedSorting, setUserClearedSorting] = useState(initialFilters.userClearedSorting);

  // Add pagination state
  const [page, setPage] = useState(initialFilters.page);
  const [rowsPerPage, setRowsPerPage] = useState(initialFilters.rowsPerPage);

  // Categorized view state - separate storage for All Tasks vs Today Tasks
  const [isCategorizedView, setIsCategorizedView] = useState(() => {
    try {
      const storageKey = isTodayView ? 'tasks-today-categorized-view' : 'tasks-all-categorized-view';
      const saved = localStorage.getItem(storageKey);
      return saved ? JSON.parse(saved) : false;
    } catch (error) {
      console.error('Error loading categorized view setting:', error);
      return false;
    }
  });
  const [expandedCategories, setExpandedCategories] = useState(new Set());
  
  // Category pagination state
  const [categoryPagination, setCategoryPagination] = useState({}); // { categoryName: { page: number, perPage: number, total: number } }
  const [categoryTasks, setCategoryTasks] = useState({}); // { categoryName: task[] }
  const [loadingCategories, setLoadingCategories] = useState(new Set()); // Set of categories currently loading
  
  // Category order state for persistence
  const [categoryOrder, setCategoryOrder] = useState(() => {
    try {
      const storageKey = isTodayView ? 'tasks-today-category-order' : 'tasks-all-category-order';
      const saved = localStorage.getItem(storageKey);
      const order = saved ? JSON.parse(saved) : [];
      return order;
    } catch (error) {
      console.error('Error loading category order:', error);
      return [];
    }
  });
  
  

  // Use the useMobile hook to determine screen size
  const isMobile = useMobile();

  // Setup drag-and-drop sensors
  const sensors = useSortableConfig(8);

  // Add the style element for the pulse animation
  useEffect(() => {
    const styleElement = document.createElement("style");
    styleElement.innerHTML = pulseAnimation;
    document.head.appendChild(styleElement);

    return () => {
      document.head.removeChild(styleElement);
    };
  }, []);

  // Scroll detection for bulk action sheet positioning
  useEffect(() => {
    let scrollTimeout;
    
    const handleScroll = () => {
      // Clear existing timeout
      if (scrollTimeout) {
        clearTimeout(scrollTimeout);
      }
      
      // Debounce the scroll handler
      scrollTimeout = setTimeout(() => {
        const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
        const windowHeight = window.innerHeight;
        const documentHeight = document.documentElement.scrollHeight;
        
        // Check if we're at the bottom (within 100px for more stable detection)
        const isBottom = scrollTop + windowHeight >= documentHeight - 100;
        
        // Only update state if the value has actually changed
        if (isBottom !== scrollRef.current) {
          scrollRef.current = isBottom;
          setIsAtBottom(isBottom);
        }
      }, 150); // Increased debounce to 150ms for more stability
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    handleScroll(); // Check initial position

    return () => {
      window.removeEventListener('scroll', handleScroll);
      if (scrollTimeout) {
        clearTimeout(scrollTimeout);
      }
    };
  }, []);

  // Auto-refresh functionality
  const refreshTasks = async (showToast = true) => {
    if (isRefreshing) return;
    
    setIsRefreshing(true);
    try {
      // Use Inertia router to reload - filters and page are in session (no URL parameters)
      const route = isTodayView ? '/tasks/today' : '/tasks';
      
      router.get(route, {}, {
        preserveState: true,
        preserveScroll: true,
        only: ['tasks', 'currentFilters'],
      });
      
      setLastRefreshTime(new Date());
      
      // Show a subtle notification for auto-refresh (not for manual refresh)
      if (autoRefreshEnabled && showToast) {
        toast({
          title: "Tasks Updated",
          description: "Tasks have been automatically refreshed.",
          duration: 2000,
        });
      }
    } catch (error) {
      console.error('Error refreshing tasks:', error);
    } finally {
      setIsRefreshing(false);
    }
  };

  // Auto-refresh effect
  useEffect(() => {
    if (!autoRefreshEnabled) return;

    const interval = setInterval(() => {
      refreshTasks();
    }, refreshInterval);

    return () => clearInterval(interval);
  }, [autoRefreshEnabled, refreshInterval]);

  // Manual refresh function
  const handleManualRefresh = async () => {
    await refreshTasks();
    toast({
      title: "Tasks Refreshed",
      description: "Tasks have been manually refreshed.",
      duration: 2000,
    });
  };

  // Handle auto-refresh toggle with interval selection
  const handleAutoRefreshToggle = () => {
    if (autoRefreshEnabled) {
      // Turning off auto-refresh
      setAutoRefreshEnabled(false);
    } else {
      // Turning on auto-refresh - show interval selection popup
      setShowIntervalPopup(true);
      setAutoRefreshEnabled(true);
    }
  };

  // Handle interval selection from popup
  const handleIntervalSelection = (minutes) => {
    setLastSelectedInterval(minutes);
    setRefreshInterval(minutes * 60 * 1000);
    setAutoRefreshEnabled(true);
    setShowIntervalPopup(false);
    
    toast({
      title: "Auto-refresh Enabled",
      description: `Tasks will refresh every ${minutes} minute${minutes !== 1 ? 's' : ''}.`,
      duration: 3000,
    });
  };

  // Monitor tasks state changes
  useEffect(() => {
    tasks.forEach((task, index) => {
    });
  }, [tasks]);

  // Keep detailTask in sync with the task context
  useEffect(() => {
    if (detailTask && isDetailOpen) {
      // Find the updated task in the context
      const updatedTask = tasks.find(t => t.id === detailTask.id);
      if (updatedTask) {
        // Update detailTask with the latest data from context
        setDetailTask(updatedTask);
      }
    }
  }, [tasks, detailTask, isDetailOpen]);

  // Track whether the modal was opened from URL parameter
  const [wasOpenedFromUrl, setWasOpenedFromUrl] = useState(false);

  // Auto-open task detail modal when task parameter is provided
  useEffect(() => {
    if (task) {
      // Find the task in the current tasks list
      const foundTask = tasks.find(t => t.id === task.id);
      if (foundTask) {
        setDetailTask(foundTask);
        setIsDetailOpen(true);
        setWasOpenedFromUrl(true);
      } else {
        // If task is not in the current user's tasks, add it temporarily
        setDetailTask(task);
        setIsDetailOpen(true);
        setWasOpenedFromUrl(true);
      }
    }
  }, [task, tasks]);

  // Add dateRange state to track the selected date range
  const [dateRange, setDateRange] = useState(initialFilters.dateRange);

  // Helper function to apply filters using POST (stores in session, keeps URL clean)
  const applyFilters = (updates = {}, resetPage = true) => {
    // Clear category tasks when filters change (they need to be refetched)
    if (Object.keys(updates).length > 0) {
      setCategoryTasks({});
      setCategoryPagination({});
      setExpandedCategories(new Set());
    }
    const currentFilters = {
      search: searchQuery,
      status: statusFilter,
      priority: priorityFilter === 'All' ? '' : priorityFilter,
      category: categoryFilter === 'All' ? '' : categoryFilter,
      acknowledged: showUnacknowledgedOnly ? 'unacknowledged' : '',
      date_from: dateRange?.from ? format(dateRange.from, 'yyyy-MM-dd') : '',
      date_to: dateRange?.to ? format(dateRange.to, 'yyyy-MM-dd') : '',
      sort_column: sortCriteria.length > 0 ? sortCriteria[0].column : 'created_at',
      sort_direction: sortCriteria.length > 0 ? sortCriteria[0].direction : 'desc',
      per_page: rowsPerPage,
      is_categorized_view: isCategorizedView,
    };

    // Apply updates
    const newFilters = { ...currentFilters, ...updates };
    
    // Remove empty values
    Object.keys(newFilters).forEach(key => {
      if (newFilters[key] === '' || newFilters[key] === null || newFilters[key] === undefined) {
        delete newFilters[key];
      }
    });

    // Remove empty status array
    if (Array.isArray(newFilters.status) && newFilters.status.length === 0) {
      delete newFilters.status;
    }

    // Use POST to store filters in session (won't show in URL)
    const filterRoute = isTodayView ? '/tasks/today/filters' : '/tasks/filters';
    const targetPage = resetPage ? 1 : (page + 1); // Convert to 1-based
    
    // Include initialCategoryTasks when in categorized view
    const onlyProps = ['tasks', 'currentFilters', 'categoryCounts'];
    if (isCategorizedView) {
      onlyProps.push('initialCategoryTasks');
    }

    router.post(filterRoute, {
      filters: newFilters,
      page: targetPage,
    }, {
      preserveState: true,
      preserveScroll: true,
      only: onlyProps,
    });
  };

  // Debounced search handler
  const searchTimeoutRef = useRef(null);
  const handleSearchChange = (value) => {
    setSearchQuery(value);
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }
    searchTimeoutRef.current = setTimeout(() => {
      applyFilters({ search: value }, true);
    }, 500); // 500ms debounce
  };

  // Filter change handlers
  const handleStatusFilterChange = (newStatusFilter) => {
    console.log('[handleStatusFilterChange] Called with:', newStatusFilter);
    console.log('[handleStatusFilterChange] Current statusFilter:', statusFilter);
    setStatusFilter(newStatusFilter);
    console.log('[handleStatusFilterChange] Calling applyFilters...');
    applyFilters({ status: newStatusFilter }, true);
  };

  const handlePriorityFilterChange = (newPriorityFilter) => {
    console.log('[handlePriorityFilterChange] Called with:', newPriorityFilter);
    console.log('[handlePriorityFilterChange] Current priorityFilter:', priorityFilter);
    setPriorityFilter(newPriorityFilter);
    console.log('[handlePriorityFilterChange] Calling applyFilters...');
    applyFilters({ priority: newPriorityFilter === 'All' ? '' : newPriorityFilter }, true);
  };

  const handleCategoryFilterChange = (newCategoryFilter) => {
    console.log('[handleCategoryFilterChange] Called with:', newCategoryFilter);
    console.log('[handleCategoryFilterChange] Current categoryFilter:', categoryFilter);
    setCategoryFilter(newCategoryFilter);
    console.log('[handleCategoryFilterChange] Calling applyFilters...');
    applyFilters({ category: newCategoryFilter === 'All' ? '' : newCategoryFilter }, true);
  };

  // Handler for acknowledged filter toggle
  const handleUnacknowledgedOnlyChange = (checked) => {
    setShowUnacknowledgedOnly(checked);
    applyFilters({
      acknowledged: checked ? 'unacknowledged' : ''
    }, true);
  };

  const handleDateRangeChange = (newDateRange) => {
    setDateRange(newDateRange);
    applyFilters({
      date_from: newDateRange?.from ? format(newDateRange.from, 'yyyy-MM-dd') : '',
      date_to: newDateRange?.to ? format(newDateRange.to, 'yyyy-MM-dd') : '',
    }, true);
  };


  const handleRowsPerPageChange = (newRowsPerPage) => {
    setRowsPerPage(newRowsPerPage);
    applyFilters({ per_page: newRowsPerPage }, true);
  };

  const handlePageChange = (newPage) => {
    // newPage should be 1-based (from pagination buttons)
    const pageIndex = typeof newPage === 'number' ? newPage : 1;
    setPage(pageIndex - 1); // Convert to 0-based for local state
    
    // Use POST to store page in session (won't show in URL)
    const filterRoute = isTodayView ? '/tasks/today/filters' : '/tasks/filters';
    const currentFilters = {
      search: searchQuery,
      status: statusFilter,
      priority: priorityFilter === 'All' ? '' : priorityFilter,
      category: categoryFilter === 'All' ? '' : categoryFilter,
      acknowledged: showUnacknowledgedOnly ? 'unacknowledged' : '',
      date_from: dateRange?.from ? format(dateRange.from, 'yyyy-MM-dd') : '',
      date_to: dateRange?.to ? format(dateRange.to, 'yyyy-MM-dd') : '',
      sort_column: sortCriteria.length > 0 ? sortCriteria[0].column : 'created_at',
      sort_direction: sortCriteria.length > 0 ? sortCriteria[0].direction : 'desc',
      per_page: rowsPerPage,
      page: pageIndex, // 1-based for server
    };
    
    // Remove empty values
    Object.keys(currentFilters).forEach(key => {
      if (currentFilters[key] === '' || currentFilters[key] === null || currentFilters[key] === undefined) {
        delete currentFilters[key];
      }
    });
    
    if (Array.isArray(currentFilters.status) && currentFilters.status.length === 0) {
      delete currentFilters.status;
    }
    
    router.post(filterRoute, {
      filters: currentFilters,
      page: pageIndex,
    }, {
      preserveState: true,
      preserveScroll: true,
      only: ['tasks', 'currentFilters'],
    });
  };

  // Fetch tasks for a specific category
  const fetchCategoryTasks = (category, page = 1) => {
    // Add to loading set
    setLoadingCategories(prev => new Set(prev).add(category));
    
    const filterRoute = isTodayView ? '/tasks/today/filters' : '/tasks/filters';
    const currentFilters = {
      search: searchQuery,
      status: statusFilter,
      priority: priorityFilter === 'All' ? '' : priorityFilter,
      category: categoryFilter === 'All' ? '' : categoryFilter,
      acknowledged: showUnacknowledgedOnly ? 'unacknowledged' : '',
      date_from: dateRange?.from ? format(dateRange.from, 'yyyy-MM-dd') : '',
      date_to: dateRange?.to ? format(dateRange.to, 'yyyy-MM-dd') : '',
      sort_column: sortCriteria.length > 0 ? sortCriteria[0].column : 'created_at',
      sort_direction: sortCriteria.length > 0 ? sortCriteria[0].direction : 'desc',
      per_page: rowsPerPage,
      page: 1, // Main page stays at 1
      category_pagination: category,
      category_page: page,
      category_per_page: rowsPerPage,
    };
    
    // Remove empty values
    Object.keys(currentFilters).forEach(key => {
      if (currentFilters[key] === '' || currentFilters[key] === null || currentFilters[key] === undefined) {
        delete currentFilters[key];
      }
    });
    
    if (Array.isArray(currentFilters.status) && currentFilters.status.length === 0) {
      delete currentFilters.status;
    }
    
    router.post(filterRoute, {
      filters: currentFilters,
    }, {
      preserveState: true,
      preserveScroll: true,
      only: ['tasks', 'currentFilters', 'categoryTasks', 'categoryCounts'],
    });
  };

  // Save filters whenever they change (but don't trigger navigation on initial load)
  useEffect(() => {
    const filtersToSave = {
      searchQuery,
      statusFilter,
      priorityFilter,
      categoryFilter,
      showUnacknowledgedOnly,
      dateRange,
      sortCriteria,
      userClearedSorting,
      rowsPerPage,
      page,
      showSubtasks,
    };
    saveFilters(filtersToSave);
  }, [searchQuery, statusFilter, priorityFilter, categoryFilter, showUnacknowledgedOnly, dateRange, sortCriteria, userClearedSorting, rowsPerPage, page, showSubtasks]);

  // Save categorized view setting
  useEffect(() => {
    try {
      localStorage.setItem('tasks-categorized-view', JSON.stringify(isCategorizedView));
    } catch (error) {
      console.error('Error saving categorized view setting:', error);
    }
  }, [isCategorizedView]);

  // Save category order to localStorage
  useEffect(() => {
    try {
      const storageKey = isTodayView ? 'tasks-today-category-order' : 'tasks-all-category-order';
      localStorage.setItem(storageKey, JSON.stringify(categoryOrder));
    } catch (error) {
      console.error('Error saving category order:', error);
    }
  }, [categoryOrder, isTodayView]);
    // Get parent tasks for dropdown
  const parentTasks = tasks
    .filter((task) => task.isParent)
    .map((task) => ({
      id: task.id,
      name: task.name,
    }));

  // Helper function to get effective due date (prioritizes defer_date)
  const getEffectiveDueDate = (task) => {
    return task.defer_date || task.due || task.dueDate;
  };

  // Parse date-like task values as local calendar dates first (YYYY-MM-DD prefix)
  // so timezone offsets do not shift the displayed day.
  const parseTaskCalendarDate = (value) => {
    if (!value) return null;
    const raw = String(value).trim();
    const match = raw.match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (match) {
      const year = Number(match[1]);
      const month = Number(match[2]);
      const day = Number(match[3]);
      const date = new Date(year, month - 1, day);
      return Number.isNaN(date.getTime()) ? null : date;
    }

    const fallback = new Date(raw);
    return Number.isNaN(fallback.getTime()) ? null : fallback;
  };

  // Helper function to check if a task is due today or overdue (for today's view)
  const isTaskDueTodayOrOverdue = (task) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    // Check if task has a due date
    if (task.due) {
      const dueDate = parseTaskCalendarDate(task.due);
      if (!dueDate) return false;
      dueDate.setHours(0, 0, 0, 0);
      // Task is due today or overdue
      if (dueDate <= today) {
        return true;
      }
    }
    
    // Check if task has a defer_date
    if (task.defer_date) {
      const deferDate = parseTaskCalendarDate(task.defer_date);
      if (!deferDate) return false;
      deferDate.setHours(0, 0, 0, 0);
      // Task is deferred to today or earlier (overdue)
      if (deferDate <= today) {
        return true;
      }
    }
    
    return false;
  };

  // Tasks are now filtered server-side, so we use tasks from the paginator
  // Filter out missed tasks from the main task list (this is still done client-side as it's a display preference)
  const filteredTasksByRole = tasks.filter(task => task.status !== "Missed");
  
  // For server-side pagination, we use the tasks directly from the context (which comes from paginator.data)
  // Apply client-side filtering only for subtask display logic
  const filteredTasks = (() => {
    const result = [];
    
    filteredTasksByRole.forEach(task => {
      // Don't show subtasks in the main list if they're shown under their parents
      if (showSubtasks && task.parent_id) return;
      
      // For server-side pagination, we include all tasks that came from the server
      // Subtask filtering is handled on the server, so we just need to structure them properly
      result.push({
        ...task,
        isFilteredParent: true
      });
    });
    
    return result;
  })();

  // Get unique categories from all tasks (independent of other filters)
  const activeCategories = [...new Set(tasks.map(task => task.category).filter(Boolean))].sort();

  // Initialize category order when new categories are detected
  useEffect(() => {
    if (isCategorizedView && activeCategories.length > 0) {
      setCategoryOrder(prevOrder => {
        // If no order exists or order is empty, initialize with current categories
        if (prevOrder.length === 0) {
          return [...activeCategories];
        }
        
        // Add any new categories that aren't in the current order
        const newCategories = activeCategories.filter(cat => !prevOrder.includes(cat));
        if (newCategories.length > 0) {
          return [...prevOrder, ...newCategories];
        }
        
        return prevOrder;
      });
    }
  }, [activeCategories, isCategorizedView]);

  // Group tasks by category for categorized view
  const groupTasksByCategory = (tasks) => {
    const grouped = {};
    tasks.forEach(task => {
      const category = task.category || 'Uncategorized';
      if (!grouped[category]) {
        grouped[category] = [];
      }
      grouped[category].push(task);
    });
    return grouped;
  };

  // Create categorized task list
  const createCategorizedTaskList = () => {
    const result = [];
    
    // Get category counts from backend (props.categoryCounts) or fallback to client-side grouping
    const categoryCountsFromBackend = props.categoryCounts || {};
    const hasBackendCounts = Object.keys(categoryCountsFromBackend).length > 0;
    
    // Get all available categories from backend counts or client-side grouping
    let allCategories;
    if (hasBackendCounts) {
      allCategories = Object.keys(categoryCountsFromBackend);
    } else {
      const grouped = groupTasksByCategory(filteredTasks);
      allCategories = Object.keys(grouped);
    }
    
    // Use custom order if available, otherwise fall back to alphabetical
    let sortedCategories;
    if (categoryOrder.length > 0) {
      // Use custom order, but include any new categories not in the order
      const orderedCategories = categoryOrder.filter(cat => allCategories.includes(cat));
      const newCategories = allCategories.filter(cat => !categoryOrder.includes(cat)).sort();
      sortedCategories = [...orderedCategories, ...newCategories];
    } else {
      // Fall back to alphabetical sorting
      sortedCategories = allCategories.sort();
    }
    
    // Add categories in their custom order, with tasks if expanded
    sortedCategories.forEach(category => {
      const isExpanded = expandedCategories.has(category);
      
      // Get task count from backend or fallback
      const taskCount = hasBackendCounts 
        ? (categoryCountsFromBackend[category] || 0)
        : (groupTasksByCategory(filteredTasks)[category]?.length || 0);
      
      // Add category header
      result.push({
        type: 'category',
        category,
        taskCount: taskCount,
        isExpanded: isExpanded
      });
      
      // Add tasks if expanded - use categoryTasks from state if available
      if (isExpanded) {
        const tasksToShow = categoryTasks[category] || [];
        tasksToShow.forEach(task => {
          result.push({
            type: 'task',
            ...task
          });
        });
      }
    });
    
    return result;
  };

  const categorizedTasks = isCategorizedView ? createCategorizedTaskList() : filteredTasks;

  // Sort the tasks (either filtered or categorized)
  const sortedTasks = isCategorizedView ? 
    // For categorized view, maintain the grouped structure but sort tasks within each group
    (() => {
      const result = [];
      let currentGroup = [];
      
      categorizedTasks.forEach(item => {
        if (item.type === 'category') {
          // If we have a current group, sort it and add to result
          if (currentGroup.length > 0) {
            const sortedGroup = currentGroup.sort((a, b) => {
              if (sortCriteria.length === 0) return 0;
              
              for (const sortCriterion of sortCriteria) {
                let comparison = 0;
                switch (sortCriterion.column) {
                  case "id":
                    comparison = String(a.id).localeCompare(String(b.id));
                    break;
                  case "name":
                    comparison = a.name.localeCompare(b.name);
                    break;
                  case "status":
                    comparison = a.status.localeCompare(b.status);
                    break;
                  case "priority":
                    comparison = Number.parseInt(a.priority) - Number.parseInt(b.priority);
                    break;
                  case "assignee":
                    const aAssignee = a.users && a.users.length > 0 ? a.users[0].name : '';
                    const bAssignee = b.users && b.users.length > 0 ? b.users[0].name : '';
                    comparison = aAssignee.localeCompare(bAssignee);
                    break;
                  case "dueDate":
                    const aDate = a.defer_date || a.due || a.dueDate;
                    const bDate = b.defer_date || b.due || b.dueDate;
                    if (!aDate && !bDate) {
                      comparison = 0;
                    } else if (!aDate) {
                      comparison = 1;
                    } else if (!bDate) {
                      comparison = -1;
                    } else {
                      const aDateObj = parseTaskCalendarDate(aDate);
                      const bDateObj = parseTaskCalendarDate(bDate);
                      if (!aDateObj || !bDateObj) {
                        comparison = 0;
                        break;
                      }
                      aDateObj.setHours(0, 0, 0, 0);
                      bDateObj.setHours(0, 0, 0, 0);
                      comparison = aDateObj.getTime() - bDateObj.getTime();
                    }
                    break;
                  case "category":
                    comparison = (a.category || '').localeCompare(b.category || '');
                    break;
                  default:
                    comparison = 0;
                }
                if (comparison !== 0) {
                  return sortCriterion.direction === "asc" ? comparison : -comparison;
                }
              }
              return 0;
            });
            result.push(...sortedGroup);
            currentGroup = [];
          }
          // Add the category header
          result.push(item);
        } else {
          // Add task to current group
          currentGroup.push(item);
        }
      });
      
      // Sort and add any remaining tasks in the last group
      if (currentGroup.length > 0) {
        const sortedGroup = currentGroup.sort((a, b) => {
          if (sortCriteria.length === 0) return 0;
          
          for (const sortCriterion of sortCriteria) {
            let comparison = 0;
            switch (sortCriterion.column) {
              case "id":
                comparison = String(a.id).localeCompare(String(b.id));
                break;
              case "name":
                comparison = a.name.localeCompare(b.name);
                break;
              case "status":
                comparison = a.status.localeCompare(b.status);
                break;
              case "priority":
                comparison = Number.parseInt(a.priority) - Number.parseInt(b.priority);
                break;
              case "assignee":
                const aAssignee = a.users && a.users.length > 0 ? a.users[0].name : '';
                const bAssignee = b.users && b.users.length > 0 ? b.users[0].name : '';
                comparison = aAssignee.localeCompare(bAssignee);
                break;
              case "dueDate":
                const aDate = a.defer_date || a.due || a.dueDate;
                const bDate = b.defer_date || b.due || b.dueDate;
                if (!aDate && !bDate) {
                  comparison = 0;
                } else if (!aDate) {
                  comparison = 1;
                } else if (!bDate) {
                  comparison = -1;
                } else {
                  const aDateObj = parseTaskCalendarDate(aDate);
                  const bDateObj = parseTaskCalendarDate(bDate);
                  if (!aDateObj || !bDateObj) {
                    comparison = 0;
                    break;
                  }
                  aDateObj.setHours(0, 0, 0, 0);
                  bDateObj.setHours(0, 0, 0, 0);
                  comparison = aDateObj.getTime() - bDateObj.getTime();
                }
                break;
              case "category":
                comparison = (a.category || '').localeCompare(b.category || '');
                break;
              default:
                comparison = 0;
            }
            if (comparison !== 0) {
              return sortCriterion.direction === "asc" ? comparison : -comparison;
            }
          }
          return 0;
        });
        result.push(...sortedGroup);
      }
      
      return result;
    })() :
    // For flat view, use the original sorting logic
    [...categorizedTasks].sort((a, b) => {
      // If no sort criteria, return original order
      if (sortCriteria.length === 0) {
        return 0;
      }
    
      // Apply each sort criterion in order
      for (const sortCriterion of sortCriteria) {
        let comparison = 0;

        switch (sortCriterion.column) {
          case "id":
            comparison = String(a.id).localeCompare(String(b.id));
            break;
          case "name":
            comparison = a.name.localeCompare(b.name);
            break;
          case "status":
            comparison = a.status.localeCompare(b.status);
            break;
          case "priority":
            comparison = Number.parseInt(a.priority) - Number.parseInt(b.priority);
            break;
          case "assignee":
            const aAssignee = a.users && a.users.length > 0 ? a.users[0].name : '';
            const bAssignee = b.users && b.users.length > 0 ? b.users[0].name : '';
            comparison = aAssignee.localeCompare(bAssignee);
            break;
          case "dueDate":
            const aDate = a.defer_date || a.due || a.dueDate;
            const bDate = b.defer_date || b.due || b.dueDate;
            // Handle null/undefined dates by putting them at the end
            if (!aDate && !bDate) {
              comparison = 0;
            } else if (!aDate) {
              comparison = 1;
            } else if (!bDate) {
              comparison = -1;
            } else {
              // Normalize both dates to the start of the day so we only compare the calendar date
              const aDateObj = parseTaskCalendarDate(aDate);
              const bDateObj = parseTaskCalendarDate(bDate);
              if (!aDateObj || !bDateObj) {
                return 0;
              }
              aDateObj.setHours(0, 0, 0, 0);
              bDateObj.setHours(0, 0, 0, 0);
              comparison = aDateObj.getTime() - bDateObj.getTime();
            }
            break;
          case "category":
            comparison = (a.category || '').localeCompare(b.category || '');
            break;
          default:
            comparison = 0;
        }

        // If this criterion gives a result, return it
        if (comparison !== 0) {
          return sortCriterion.direction === "asc" ? comparison : -comparison;
        }
      }
    
      // If all criteria are equal, maintain original order
      return 0;
    });

  // Tasks are already paginated server-side, so we use them directly
  // For categorized view, we still show all tasks from the current page (they're already filtered/paginated on server)
  const paginatedTasks = isCategorizedView ? 
    // For categorized view, use the sorted tasks (already from current page)
    sortedTasks :
    // For flat view, tasks are already paginated from server, just use them directly
    sortedTasks;
  
  // Get pagination info from paginator
  const paginationInfo = tasksPaginator ? {
    currentPage: tasksPaginator.current_page || 1,
    lastPage: tasksPaginator.last_page || 1,
    perPage: tasksPaginator.per_page || rowsPerPage,
    total: tasksPaginator.total || 0,
    from: tasksPaginator.from || 0,
    to: tasksPaginator.to || 0,
    links: tasksPaginator.links || [],
  } : null;

  // Calculate total visible tasks (parent tasks + their subtasks) in the current page
  const getVisibleTasksCount = () => {
    let count = 0;
    paginatedTasks.forEach((item) => {
      // Skip category headers
      if (item.type === 'category') return;
      
      const task = item;
      count++; // Count the main task
      
      // For filtered subtasks, they're already counted as individual items
      if (task.isFilteredSubtask) {
        return; // Already counted as 1
      }
      
      // For regular parent tasks, count their expanded subtasks
      if (
        task.isParent &&
        task.subtasks &&
        task.subtasks.length > 0 &&
        expandedTasks.includes(task.id)
      ) {
        // Only count subtasks that aren't filtered subtasks (since those are shown separately)
        const nonFilteredSubtasks = task.subtasks.filter(subtask => !subtask.isFilteredSubtask);
        count += nonFilteredSubtasks.length;
      }
    });
    return count;
  };

  // Calculate total tasks including all subtasks
  const getTotalTasksCount = () => {
    if (isCategorizedView) {
      // For categorized view, sum up category counts from backend
      const categoryCountsFromBackend = props.categoryCounts || {};
      const totalCount = Object.values(categoryCountsFromBackend).reduce((sum, count) => sum + (count || 0), 0);
      // If no category counts available yet, return 0 (will update when backend responds)
      return totalCount;
    }
    
    // Prefer server-reported total (already reflects filters/pagination)
    if (paginationInfo) {
      return paginationInfo.total || 0;
    }
    
    // For flat view, use the original logic
    let count = 0;
    sortedTasks.forEach((item) => {
      // Skip category headers
      if (item.type === 'category') return;
      
      const task = item;
      count++; // Count the main task
      
      // For filtered subtasks, they're already counted as individual items
      if (task.isFilteredSubtask) {
        return; // Already counted as 1
      }
      
      // For regular parent tasks, count their subtasks
      if (task.isParent && task.subtasks && task.subtasks.length > 0) {
        // Only count subtasks that aren't filtered subtasks (since those are shown separately)
        const nonFilteredSubtasks = task.subtasks.filter(subtask => !subtask.isFilteredSubtask);
        count += nonFilteredSubtasks.length;
      }
    });
    return count;
  };


  const toggleTaskSelection = (taskId) => {
    setSelectedTasks((prev) =>
      prev.includes(taskId)
        ? prev.filter((id) => id !== taskId)
        : [...prev, taskId]
    );
  };

  const toggleAllTasks = () => {
    const taskItems = isCategorizedView 
      ? sortedTasks.filter(item => item.type === 'task')
      : filteredTasks;
    
    if (selectedTasks.length === taskItems.length) {
      setSelectedTasks([]);
    } else {
      setSelectedTasks(taskItems.map((task) => task.id));
    }
  };

  const toggleExpandTask = (taskId) => {
    setExpandedTasks((prev) =>
      prev.includes(taskId)
        ? prev.filter((id) => id !== taskId)
        : [...prev, taskId]
    );
  };

  const toggleCategoryExpansion = (category) => {
    setExpandedCategories((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(category)) {
        newSet.delete(category);
      } else {
        newSet.add(category);
        // Fetch tasks if not already loaded
        if (!categoryTasks[category] || categoryTasks[category].length === 0) {
          const pagination = categoryPagination[category];
          const page = pagination?.page || 1;
          fetchCategoryTasks(category, page);
        }
      }
      return newSet;
    });
  };

  const toggleCategorizedView = () => {
    const newValue = !isCategorizedView;
    
    // Save to separate localStorage keys
    try {
      const storageKey = isTodayView ? 'tasks-today-categorized-view' : 'tasks-all-categorized-view';
      localStorage.setItem(storageKey, JSON.stringify(newValue));
    } catch (error) {
      console.error('Error saving categorized view setting:', error);
    }
    
    // Prepare filters with is_categorized_view flag
    const currentFilters = {
      search: searchQuery,
      status: statusFilter,
      priority: priorityFilter === 'All' ? '' : priorityFilter,
      category: categoryFilter === 'All' ? '' : categoryFilter,
      acknowledged: showUnacknowledgedOnly ? 'unacknowledged' : '',
      date_from: dateRange?.from ? format(dateRange.from, 'yyyy-MM-dd') : '',
      date_to: dateRange?.to ? format(dateRange.to, 'yyyy-MM-dd') : '',
      sort_column: sortCriteria.length > 0 ? sortCriteria[0].column : 'created_at',
      sort_direction: sortCriteria.length > 0 ? sortCriteria[0].direction : 'desc',
      per_page: rowsPerPage,
      is_categorized_view: newValue,
    };
    
    // Remove empty values
    Object.keys(currentFilters).forEach(key => {
      if (currentFilters[key] === '' || currentFilters[key] === null || currentFilters[key] === undefined) {
        delete currentFilters[key];
      }
    });
    
    // Remove empty status array
    if (Array.isArray(currentFilters.status) && currentFilters.status.length === 0) {
      delete currentFilters.status;
    }
    
    // Use POST to store filters in session and trigger server-side rendering
    const filterRoute = isTodayView ? '/tasks/today/filters' : '/tasks/filters';
    
    // Make Inertia request - view will update after request completes
    router.post(filterRoute, {
      filters: currentFilters,
      page: 1, // Reset to first page when switching views
    }, {
      preserveState: true,
      preserveScroll: true,
      only: ['tasks', 'currentFilters', 'categoryCounts', 'initialCategoryTasks'],
      onSuccess: () => {
        // Update local state after successful request
        setIsCategorizedView(newValue);
        setPage(0);
        
        // Clear category tasks when switching to flat view
        if (!newValue) {
          setCategoryTasks({});
          setCategoryPagination({});
          setExpandedCategories(new Set());
        }
      },
    });
  };

  // Drag and drop handler for category reordering using @dnd-kit
  const handleCategoryDragEnd = (event) => {
    const { active, over } = event;

    if (over && active.id !== over.id) {
      setCategoryOrder((items) => {
        const oldIndex = items.indexOf(active.id);
        const newIndex = items.indexOf(over.id);
        return arrayMove(items, oldIndex, newIndex);
      });
    }
  };


  const getStatusColor = (status) => {
    switch (status) {
      case "Completed":
        return "bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400";
      case "In Progress":
        return "bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400";
      case "To Do":
        return "bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-400";
      case "In Review":
        return "bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-400";
      default:
        return "bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-400";
    }
  };

  const getPriorityColor = (priority) => {
    const priorityNum = Number.parseInt(priority, 10);
    if (priorityNum >= 8) {
      return "bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400";
    } else if (priorityNum >= 5) {
      return "bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400";
    } else {
      return "bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400";
    }
  };

  const isTaskOverdue = (task) => {
    const effectiveDate = getEffectiveDueDate(task);
    if (!effectiveDate || effectiveDate === "") return false;
    
    const taskDate = parseTaskCalendarDate(effectiveDate);
    if (!taskDate) return false;
    
    // A task is overdue if it's due before today (not including today)
    const todayStart = new Date(today);
    todayStart.setHours(0, 0, 0, 0);
    
    return taskDate < todayStart;
  };

  const handleOpenNewTaskForm = () => {
    setSelectedTask(undefined);
    setIsFormOpen(true);
  };

  const handleTaskClick = (task) => {
    setSelectedTask({
      id: task.id,
      name: task.name,
      description: task.description,
      category: task.category,
      priority: task.priority,
      due: task.defer_date || task.due || task.dueDate,
      status: task.status,
      deferrable: task.deferrable,
      isParent: task.isParent,
      parentId: task.parent_id || null,
      parentName: task.parentName || null,
      taskType: task.taskType,
      taskTypeCategories: task.taskTypeCategories,
      salesProbability: task.salesProbability,
    });
    setIsFormOpen(true);
  };

  const handleOpenTaskDetail = (task) => {
    // Find the full task with subtasks and notes - first check main tasks array, then categoryTasks
    let fullTask = tasks.find((t) => t.id === task.id);
    
    // If not found in main tasks array, search in categoryTasks
    if (!fullTask && isCategorizedView) {
      const allCategoryTasks = Object.values(categoryTasks || {}).flat();
      fullTask = allCategoryTasks.find((t) => t.id === task.id);
    }

    if (fullTask) {
      // If this is a subtask, make sure we have the parent information
      if (!fullTask.isParent && fullTask.parent_id) {
        // Search for parent in both main tasks and categoryTasks
        let parentTask = tasks.find((t) => t.id === fullTask.parent_id);
        if (!parentTask && isCategorizedView) {
          const allCategoryTasks = Object.values(categoryTasks || {}).flat();
          parentTask = allCategoryTasks.find((t) => t.id === fullTask.parent_id);
        }
        
        if (parentTask) {
          // Ensure the parent name is set correctly
          const taskWithParent = {
            ...fullTask,
            parentName: parentTask.name,
          };
          setDetailTask(taskWithParent);
        } else {
          setDetailTask(fullTask);
        }
      } else {
        setDetailTask(fullTask);
      }

      setIsDetailOpen(true);
      setWasOpenedFromUrl(false); // Reset flag when opened manually
    }
  };

  const handleViewParentTask = (parentId) => {
    const parentTask = tasks.find((t) => t.id === parentId);
    if (parentTask) {
      // Make sure we have the complete parent task with all subtasks
      const fullParentTask = {
        ...parentTask,
        subtasks: tasks.filter((task) => task.parent_id === parentId),
      };
      setDetailTask(fullParentTask);
      setIsDetailOpen(true);
    }
  };

  const handleTaskFormSubmit = async (formData) => {
    setIsSubmitting(true);

    try {
      if (formData.id) {
        // Update existing task - call backend API
        await router.put(`/tasks/${formData.id}`, {
          name: formData.name,
          description: formData.description,
          category: formData.category,
          priority: formData.priority,
          due: formData.due,
          status: formData.status,
          deferrable: formData.deferrable,
          is_parent: formData.isParent,
          parent_id: formData.isParent
            ? null
            : formData.parentId || null,
          parentName: formData.isParent
            ? undefined
            : formData.parentName || undefined,
          taskType: formData.taskType,
          taskTypeCategories: formData.taskTypeCategories,
          salesProbability: formData.salesProbability,
        });
        
        // Close form and reset submitting state for updates
        setIsFormOpen(false);
        setIsSubmitting(false);
      } else {
        // Create new task - optimistic UI update first
        const optimisticTask = createOptimisticTask(formData, currentUser, users);
        addTask(optimisticTask);
        
        // Don't close form immediately - let the modal handle its own closure
        // setIsFormOpen(false);

        try {
          // Extract assignment data
          const assignment = formData.assignment || { type: 'individual', user_ids: [], department_ids: [] }
          
          await router.post('/tasks', {
            name: formData.name,
            description: formData.description,
            category: formData.category,
            priority: formData.priority,
            due: formData.due,
            deferrable: formData.deferrable,
            missable: formData.missable,
            task_type_categories: formData.taskTypeCategories || [],
            checklist: formData.checklist || [],
            link: formData.link || '',
            link_title: formData.linkTitle || '',
            assigned_user_ids: assignment.type === 'individual' ? (assignment.user_ids || []) : (formData.assignedUserIds || []),
            assignment_type: assignment.type || 'individual',
            assigned_department_ids: (assignment.type === 'department' || assignment.type === 'department_manager') ? (assignment.department_ids || []) : [],
            is_shared: !!assignment.shared,
            is_parent: formData.isParent,
            parent_id: formData.isParent ? null : formData.parentId || null,
            workflow_id: formData.workflowId || null,
            trigger_type: formData.selectedTrigger || null,
            trigger_config: formData.triggerConfig || null,
            create_task_now: formData.createTaskNow !== undefined ? formData.createTaskNow : true,
          }, {
            preserveScroll: true,
            onSuccess: (page) => {
              // The page refresh will replace the optimistic task with real data
              router.reload({ only: ['tasks'], preserveScroll: true });
            },
            onError: (errors) => {
              console.error('Backend task creation failed:', errors);
              // Remove the optimistic task since backend failed
              deleteTask(optimisticTask.id);
              // Show error message
              toast({
                title: "Error",
                description: "Failed to create task. Please try again.",
                variant: "destructive",
              });
              // Reset submitting state on error
              setIsSubmitting(false);
            }
          });
        } catch (error) {
          console.error('Error submitting task to backend:', error);
          // Remove the optimistic task since backend failed
          deleteTask(optimisticTask.id);
          // Show error message
          toast({
            title: "Error", 
            description: "Failed to create task. Please try again.",
            variant: "destructive",
          });
          // Reset submitting state on error
          setIsSubmitting(false);
        }

        return; // Exit early since we handled everything above
      }
      
    } catch (error) {
      console.error('Error submitting task:', error);
      setIsSubmitting(false);
      // Keep form open on error so user can retry
    }
  };

  // Remove a task from local state (and from any parent subtasks) when it should be hidden
  const removeTaskFromView = (taskId) => {
    setTasks(prevTasks => {
      return prevTasks.reduce((acc, task) => {
        // Skip the task itself
        if (task.id === taskId) {
          return acc;
        }

        // Remove the task from any parent's subtasks array
        if (task.subtasks && task.subtasks.length > 0) {
          const updatedSubtasks = task.subtasks.filter(subtask => subtask.id !== taskId);
          if (updatedSubtasks.length !== task.subtasks.length) {
            acc.push({ ...task, subtasks: updatedSubtasks });
            return acc;
          }
        }

        acc.push(task);
        return acc;
      }, []);
    });
  };

  const handleCompleteTask = async (taskId, formResponses = null) => {
    setIsSubmitting(true);

    // Keep original values to allow reverting on error
    const currentTask = tasks.find(t => t.id === taskId);
    const originalStatus = currentTask?.status || 'In Progress';
    const originalCompletedDate = currentTask?.completed_date || null;

    try {
      // Optimistic update for smooth UX
      const optimisticCompletedDate = new Date().toISOString();
      updateTask(taskId, { 
        status: 'Completed',
        completed_date: optimisticCompletedDate
      });

      // Ensure subtasks reflect the optimistic completion
      setTasks(prevTasks => {
        return prevTasks.map(task => {
          if (task.id === taskId) {
            return { ...task, status: 'Completed', completed_date: optimisticCompletedDate };
          }

          if (task.subtasks) {
            const updatedSubtasks = task.subtasks.map(subtask =>
              subtask.id === taskId
                ? { ...subtask, status: 'Completed', completed_date: optimisticCompletedDate }
                : subtask
            );

            if (updatedSubtasks.some(subtask => subtask.id === taskId)) {
              return { ...task, subtasks: updatedSubtasks };
            }
          }

          return task;
        });
      });

      // Prepare request payload
      const requestBody = {
        task_id: taskId
      };

      if (formResponses && Object.keys(formResponses).length > 0) {
        requestBody.form_responses = formResponses;
      }

      // Use Inertia for the completion request
      router.post('/tasks/complete', requestBody, {
        preserveScroll: true,
        onSuccess: () => {
          // If the current filter excludes completed tasks, remove from local state immediately
          if (!statusFilter.includes("Completed")) {
            removeTaskFromView(taskId);
          }

          // Refresh tasks and filters to stay in sync with server-side pagination/filtering
          router.reload({
            only: ['tasks', 'currentFilters'],
            preserveScroll: true,
          });

          toast({
            title: "Task completed",
            description: "Task has been marked as completed.",
          });
        },
        onError: (errors) => {
          console.error('Error completing task:', errors);
          // Revert optimistic update on error
          updateTask(taskId, { 
            status: originalStatus,
            completed_date: originalCompletedDate
          });

          setTasks(prevTasks => {
            return prevTasks.map(task => {
              if (task.id === taskId) {
                return { ...task, status: originalStatus, completed_date: originalCompletedDate };
              }

              if (task.subtasks) {
                const updatedSubtasks = task.subtasks.map(subtask =>
                  subtask.id === taskId
                    ? { ...subtask, status: originalStatus, completed_date: originalCompletedDate }
                    : subtask
                );

                if (updatedSubtasks.some(subtask => subtask.id === taskId)) {
                  return { ...task, subtasks: updatedSubtasks };
                }
              }

              return task;
            });
          });

          toast({
            title: "Error",
            description: "Failed to complete task. Please try again.",
            variant: "destructive",
          });
        },
        onFinish: () => {
          setIsSubmitting(false);
        }
      });

    } catch (error) {
      console.error('Error completing task:', error);
      
      // Revert the optimistic update since backend failed unexpectedly
      updateTask(taskId, { 
        status: originalStatus,
        completed_date: originalCompletedDate || null
      });
      
      // Show error message
      toast({
        title: "Error",
        description: `Failed to complete task: ${error.message}`,
        variant: "destructive",
      });
      
      setIsSubmitting(false);
    }
  };

  const handleDeleteTask = async (taskId) => {
    setIsSubmitting(true);

    try {
      const response = await fetch('/tasks/bulk-delete', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-TOKEN': csrf_token,
        },
        body: JSON.stringify({ task_ids: [taskId] }),
      });

      const data = await response.json();

      if (response.ok && data.success) {
        deleteTask(taskId);
        toast({
          title: "Success",
          description: "Task deleted successfully.",
        });
      } else {
        toast({
          title: "Error",
          description: data.message || "Failed to delete task.",
          variant: "destructive",
        });
      }
    } catch (error) {
      toast({
        title: "Error",
        description: `Failed to delete task: ${error.message}`,
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeferTask = async (taskId, deferDate) => {
    setIsSubmitting(true);

    try {
      // The date string is already in YYYY-MM-DD format from the calendar
      const formattedDate = deferDate;
      
      // Use Inertia's router for seamless updates
      router.put('/tasks', {
        id: taskId,
        due: formattedDate,
        dueDate: formattedDate,
        defer_date: formattedDate,
        deferrable: 1
      }, {
        preserveScroll: true,
        onSuccess: () => {
          // For display in the toast, create a date object with time set to noon
          const displayDate = new Date(formattedDate + 'T12:00:00');
          
          toast({
            title: "Task Deferred",
            description: `Task has been deferred to ${format(displayDate, "PPP")}`,
          });
        },
        onError: (errors) => {
          console.error('Error deferring task:', errors);
          toast({
            title: "Error",
            description: "Failed to defer task. Please try again.",
            variant: "destructive",
          });
        },
        onFinish: () => {
          setIsSubmitting(false);
        }
      });
    } catch (error) {
      console.error('Error deferring task:', error);
      toast({
        title: "Error",
        description: "Failed to defer task. Please try again.",
        variant: "destructive",
      });
      setIsSubmitting(false);
    }
  };

  const handleAddNote = (taskId, noteHtml) => {
    setIsSubmitting(true);
    // Send the HTML content directly instead of converting to plain text
    // This allows the backend to detect mention spans properly
    const note = noteHtml;
    
    // Get current task to create optimistic note
    const currentTask = tasks.find(t => t.id === taskId);
    
    const optimisticNote = {
      id: `TEMP-${Date.now()}`,
      note: note,
      created_at: new Date().toISOString(),
      user: currentUser,
      author: {
        id: currentUser.id,
        name: `${currentUser.firstName} ${currentUser.lastName}`,
        email: currentUser.email
      },
      isOptimistic: true
    };
    
    // Immediately update the task context with the new note
    if (currentTask) {
      const updatedTask = {
        ...currentTask,
        notes: [...(currentTask.notes || []), optimisticNote]
      };
      
      updateTask(taskId, updatedTask);
      } else {
      console.error('currentTask not found for taskId:', taskId);
    }
    
    // Then sync with backend
    router.post('/notes',
      { note, task_id: taskId },
      {
        preserveScroll: true,
        onSuccess: (page) => {
          // The page reload will replace the optimistic note with real data
          router.reload({ only: ['tasks'], preserveScroll: true });
          setIsSubmitting(false);
        },
        onError: (errors) => {
          // Remove the optimistic note since backend failed
          if (currentTask) {
            const revertedTask = {
              ...currentTask,
              notes: (currentTask.notes || []).filter(n => !n.isOptimistic)
            };
            updateTask(taskId, revertedTask);
          }
          
          // Show error message
          toast({
            title: "Error",
            description: "Failed to add note. Please try again.",
            variant: "destructive",
          });
          
          setIsSubmitting(false);
        }
      }
    );
  };

  const handleStatusUpdate = async (taskId, newStatus) => {
    setIsSubmitting(true);

    // Get the current task and its status before updating
    const currentTask = tasks.find(t => t.id === taskId);
    const originalStatus = currentTask ? currentTask.status : null;

    try {
      // Optimistically update the frontend
      updateTask(taskId, { status: newStatus });

      // Also update the subtask in the parent's subtasks array if this is a subtask
      const currentTask = tasks.find(t => t.id === taskId);
      if (currentTask && currentTask.parent_id) {
        setTasks(prevTasks => {
          const updatedTasks = prevTasks.map(task => {
            // Update the main task
            if (task.id === taskId) {
              return { ...task, status: newStatus };
            }
            
            // Update the parent task's subtasks array if this task is a parent
            if (task.subtasks) {
              const updatedSubtasks = task.subtasks.map(subtask => 
                subtask.id === taskId 
                  ? { ...subtask, status: newStatus }
                  : subtask
              );
              
              if (updatedSubtasks.some(subtask => subtask.id === taskId)) {
                return { ...task, subtasks: updatedSubtasks };
              }
            }
            
            return task;
          });
          
          return updatedTasks;
        });
      }

      // Call the backend API
      const response = await fetch('/tasks', {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-TOKEN': csrf_token,
        },
        body: JSON.stringify({
          id: taskId,
          status: newStatus
        })
      });

      if (!response.ok) {
        if (response.status === 419) {
          // CSRF token mismatch - try to refresh the token and retry
          
          try {
            const tokenResponse = await fetch('/token', {
              method: 'GET',
              headers: {
                'Accept': 'application/json',
              },
            });
            
            if (tokenResponse.ok) {
              const newToken = await tokenResponse.text();
              
              // Update the meta tag with the fresh token
              const metaTag = document.querySelector('meta[name="csrf-token"]');
              if (metaTag) {
                metaTag.setAttribute('content', newToken);
              }
              
              // Retry the status update with the fresh token
              const retryResponse = await fetch('/tasks', {
                method: 'PUT',
                headers: {
                  'Content-Type': 'application/json',
                  'X-CSRF-TOKEN': newToken,
                },
                body: JSON.stringify({
                  id: taskId,
                  status: newStatus
                })
              });
              
              if (!retryResponse.ok) {
                const retryResult = await retryResponse.json();
                throw new Error(retryResult.message || 'Failed to update task status after token refresh');
              }
              
              const retryResult = await retryResponse.json();
              if (!retryResult.success) {
                throw new Error(retryResult.message || 'Failed to update task status after token refresh');
              }
              
              setIsSubmitting(false);
              return;
            } else {
              throw new Error('Failed to refresh CSRF token');
            }
          } catch (tokenError) {
            console.error('Error refreshing CSRF token:', tokenError);
            toast({
              title: "Error",
              description: "Session expired. Please refresh the page and try again.",
              variant: "destructive",
            });
            throw tokenError;
          }
        } else {
          const result = await response.json();
          throw new Error(result.message || 'Failed to update task status');
        }
      } else {
        const result = await response.json();
        if (!result.success) {
          throw new Error(result.message || 'Failed to update task status');
        }
        setIsSubmitting(false);
      }

    } catch (error) {
      console.error('Error updating task status:', error);
      
      // Revert the optimistic update since backend failed
      if (originalStatus) {
        updateTask(taskId, { status: originalStatus });
        
        // Also revert the subtask status in the parent's array if this is a subtask
        const currentTask = tasks.find(t => t.id === taskId);
        if (currentTask && currentTask.parent_id) {
          setTasks(prevTasks => {
            const updatedTasks = prevTasks.map(task => {
              // Update the main task
              if (task.id === taskId) {
                return { ...task, status: originalStatus };
              }
              
              // Update the parent task's subtasks array if this task is a parent
              if (task.subtasks) {
                const updatedSubtasks = task.subtasks.map(subtask => 
                  subtask.id === taskId 
                    ? { ...subtask, status: originalStatus }
                    : subtask
                );
                
                if (updatedSubtasks.some(subtask => subtask.id === taskId)) {
                  return { ...task, subtasks: updatedSubtasks };
                }
              }
              
              return task;
            });
            
            return updatedTasks;
          });
        }
      }
      
      // Show error message
      toast({
        title: "Error",
        description: `Failed to update task status: ${error.message}`,
        variant: "destructive",
      });
      
      setIsSubmitting(false);
    }
  };

  const renderSortIndicator = (column) => {
    // Find all sort criteria for this column
    const columnSortCriteria = sortCriteria.filter(criterion => criterion.column === column);
    
    if (columnSortCriteria.length === 0) {
      return null;
    }
    
    // Show the primary sort indicator (first one)
    const primaryCriterion = columnSortCriteria[0];
    const sortOrder = sortCriteria.findIndex(criterion => criterion.column === column) + 1;
    const isSecondary = sortOrder > 1;
    
    return (
      <div className={`flex items-center ${isSecondary ? 'px-1 py-0.5 rounded' : ''}`}>
        {primaryCriterion.direction === "asc" ? (
          <ArrowUp className={`h-4 w-4 ${sortOrder === 1 ? 'text-primary' : 'text-muted-foreground'}`} />
        ) : (
          <ArrowDown className={`h-4 w-4 ${sortOrder === 1 ? 'text-primary' : 'text-muted-foreground'}`} />
        )}
        {sortOrder > 1 && (
          <span className="text-xs text-muted-foreground px-1 rounded">
            {sortOrder}
          </span>
        )}
      </div>
    );
  };

  // Handle column sorting
  const handleSort = (column, event) => {
    const isShiftClick = event && event.shiftKey;
    
    if (isShiftClick) {
      // Add or update secondary sort criterion
      setSortCriteria(prevCriteria => {
        const newCriteria = [...prevCriteria];
        const existingIndex = newCriteria.findIndex(criterion => criterion.column === column);
        
        if (existingIndex >= 0) {
          // Toggle direction of existing criterion
          newCriteria[existingIndex].direction = newCriteria[existingIndex].direction === "asc" ? "desc" : "asc";
        } else {
          // Add new criterion at the end
          newCriteria.push({ column, direction: "asc" });
        }
        
        return newCriteria;
      });
      // User is adding sorting back
      setUserClearedSorting(false);
    } else {
      // Check if this is a different column than last clicked
      const isDifferentColumn = lastClickedColumn !== column;
      
      if (isDifferentColumn) {
        // Reset cycle for new column - always start with ascending
        setLastClickedColumn(column);
        const newCriteria = [{ column, direction: "asc" }];
        setSortCriteria(newCriteria);
        setUserClearedSorting(false);
        applyFilters({
          sort_column: column,
          sort_direction: 'asc',
        }, true);
      } else {
        // Same column - cycle through: asc → desc → remove
        const existingIndex = sortCriteria.findIndex(criterion => criterion.column === column);
        
        if (existingIndex >= 0) {
          const currentDirection = sortCriteria[existingIndex].direction;
          
          if (currentDirection === "asc") {
            // Change to descending
            const newCriteria = [{ column, direction: "desc" }];
            setSortCriteria(newCriteria);
            applyFilters({
              sort_column: column,
              sort_direction: 'desc',
            }, true);
          } else if (currentDirection === "desc") {
            // Remove this sort
            setSortCriteria([]);
            setLastClickedColumn(null); // Reset last clicked when removing
            setUserClearedSorting(true);
            applyFilters({
              sort_column: 'created_at',
              sort_direction: 'desc',
            }, true);
          }
        } else {
          // Column not in sort - add as ascending
          const newCriteria = [{ column, direction: "asc" }];
          setSortCriteria(newCriteria);
          setUserClearedSorting(false);
          applyFilters({
            sort_column: column,
            sort_direction: 'asc',
          }, true);
        }
      }
    }
  };

  // Remove all sort criteria
  const removeAllSorts = () => {
    setSortCriteria([]); // Allow no sorting
    setUserClearedSorting(true); // Mark that user has manually cleared sorting
  };

  // Reset all filters
  const resetFilters = () => {
    setSearchQuery("");
    setStatusFilter(["To Do", "In Progress"]); // Reset to default status filter
    setPriorityFilter("All");
    setCategoryFilter("All");
    setShowUnacknowledgedOnly(false);
    setDateRange({ from: undefined, to: undefined }); // Clear date filter completely with undefined values
    setPage(0); // Reset to first page when clearing filters
    // Reset to no sorting and mark as user cleared
    setSortCriteria([]);
    setUserClearedSorting(true);
    // Apply filters with reset values
    applyFilters({
      search: '',
      status: ["To Do", "In Progress"],
      priority: '',
      acknowledged: '',
      category: '',
      date_from: '',
      date_to: '',
      sort_column: 'created_at',
      sort_direction: 'desc',
    }, true);
  };

  // Count active filters (priority, date filters, and status changes from default)
  const getActiveFilterCount = () => {
    let count = 0;
    if (priorityFilter !== "All") count++;
    if (categoryFilter !== "All") count++;
    if (showUnacknowledgedOnly) count++;
    if (dateRange.from != null || dateRange.to != null) count++; // Count if any date is selected (check for null/undefined)

    // Check if status filter differs from default ["To Do", "In Progress"]
    const defaultStatusFilter = ["To Do", "In Progress"];
    const isStatusFilterChanged = JSON.stringify(statusFilter.sort()) !== JSON.stringify(defaultStatusFilter.sort());
    if (isStatusFilterChanged) count++;

    return count;
  };

  // Add this helper function to properly format dates
  const formatTaskDate = (dateString) => {
    if (!dateString) return '';
    
    try {
      const date = parseTaskCalendarDate(dateString);
      if (!date) {
        console.warn('Invalid date string:', dateString);
        return dateString;
      }
      
      return format(date, "PP");
    } catch (error) {
      console.error('Error formatting date:', { dateString, error });
      return dateString;
    }
  };

  const statuses = [
    { value: "To Do", label: "To Do" },
    { value: "In Progress", label: "In Progress" },
    { value: "Completed", label: "Completed" },
  ];

  return (
    <div className="flex flex-col gap-6 pb-8 pt-6 px-4 md:px-6 w-full">
      <div className="flex flex-col gap-2">
        <h1 className="text-2xl font-bold tracking-tight flex items-center justify-between gap-4">
          <span>{isTodayView ? "Today's Tasks" : "Tasks"}</span>
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <div className="flex items-center gap-2">
                  <Switch 
                    checked={showUnacknowledgedOnly}
                    onCheckedChange={handleUnacknowledgedOnlyChange}
                  />
                  <span className="text-sm text-muted-foreground whitespace-nowrap">New tasks only</span>
                </div>
              </TooltipTrigger>
              <TooltipContent side="bottom" className="max-w-sm">
                <p className="font-normal">Show only tasks that haven't been acknowledged yet. Tasks are automatically marked as acknowledged when you open them. Turn off to see all tasks.</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </h1>
        <p className="text-muted-foreground">
          {isTodayView ? "Tasks due today or overdue" : "Manage and track all your tasks in one place"}
        </p>
      </div>

      <div className="flex flex-wrap items-center justify-between gap-4 mb-4">
        {isMobile ? (
          <div className="flex flex-col gap-2 w-full">
            <div className="relative w-full">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground h-4 w-4" />
              <Input
                placeholder="Search tasks..."
                value={searchQuery}
                onChange={(e) => handleSearchChange(e.target.value)}
                className="pl-10 h-9"
              />
            </div>
            <div className="flex items-center gap-2 w-full justify-between">
            <FilterDropdown
              onReset={resetFilters}
              count={getActiveFilterCount()}
            >
              <Popover>
                <PopoverTrigger asChild>
                  <Button variant="outline" className="w-full h-9 justify-between">
                    <span className="truncate font-normal">
                      {statusFilter.length === 0 
                        ? "All Statuses"
                        : statusFilter.join(", ")}
                    </span>
                    <ChevronDown className="ml-2 h-4 w-4 opacity-50" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-[180px] p-0" align="start">
                  <Command className="font-normal">
                    <CommandGroup>
                      <CommandItem
                        onSelect={() => handleStatusFilterChange([])}
                        className="justify-between"
                      >
                        <span>All Statuses</span>
                        {statusFilter.length === 0 && <Check className="h-4 w-4" />}
                      </CommandItem>
                      {statuses.map((status) => (
                        <CommandItem
                          key={status.value}
                          onSelect={() => {
                            const newValue = statusFilter.includes(status.value)
                              ? statusFilter.filter(s => s !== status.value)
                              : [...statusFilter, status.value];
                            handleStatusFilterChange(newValue);
                          }}
                          className="justify-between"
                        >
                          <span>{status.label}</span>
                          {statusFilter.includes(status.value) && (
                            <Check className="h-4 w-4" />
                          )}
                        </CommandItem>
                      ))}
                    </CommandGroup>
                  </Command>
                </PopoverContent>
              </Popover>

              <Select value={priorityFilter} onValueChange={handlePriorityFilterChange}>
                <SelectTrigger className="w-full h-9">
                  <SelectValue placeholder="Priority" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="All">All Priorities</SelectItem>
                  <SelectItem value="10">10 (Highest)</SelectItem>
                  <SelectItem value="9">9</SelectItem>
                  <SelectItem value="8">8</SelectItem>
                  <SelectItem value="7">7</SelectItem>
                  <SelectItem value="6">6</SelectItem>
                  <SelectItem value="5">5</SelectItem>
                  <SelectItem value="4">4</SelectItem>
                  <SelectItem value="3">3</SelectItem>
                  <SelectItem value="2">2</SelectItem>
                  <SelectItem value="1">1 (Lowest)</SelectItem>
                </SelectContent>
              </Select>

              <Select value={categoryFilter} onValueChange={handleCategoryFilterChange}>
                <SelectTrigger className="w-full h-9">
                  <SelectValue placeholder="Category" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="All">All Categories</SelectItem>
                  {activeCategories.map((category) => (
                    <SelectItem key={category} value={category}>
                      {category}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <DatePickerWithRange
                buttonClassName="h-9 text-black dark:text-white"
                dateRange={dateRange}
                onDateRangeChange={handleDateRangeChange}
              />
            </FilterDropdown>
            </div>
          </div>
        ) : (
          <>
              <div className="relative w-full sm:w-auto sm:flex-1 sm:max-w-sm mb-2 sm:mb-0">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground h-4 w-4" />
                <Input
                  placeholder="Search tasks..."
                  value={searchQuery}
                  onChange={(e) => handleSearchChange(e.target.value)}
                  className="pl-10 h-9"
                />
              </div>
              <div className="flex xl:hidden items-center gap-2 w-full sm:w-auto">
              <FilterDropdown
                onReset={resetFilters}
                count={getActiveFilterCount()}
              >
                <Popover>
                  <PopoverTrigger asChild>
                    <Button variant="outline" className="w-full h-9 justify-between">
                      <span className="truncate font-normal">
                        {statusFilter.length === 0 
                          ? "All Statuses"
                          : statusFilter.join(", ")}
                      </span>
                      <ChevronDown className="ml-2 h-4 w-4 opacity-50" />
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-[180px] p-0" align="start">
                    <Command className="font-normal">
                    <CommandGroup>
                      <CommandItem
                        onSelect={() => handleStatusFilterChange([])}
                        className="justify-between"
                      >
                        <span>All Statuses</span>
                        {statusFilter.length === 0 && <Check className="h-4 w-4" />}
                      </CommandItem>
                      {statuses.map((status) => (
                        <CommandItem
                          key={status.value}
                          onSelect={() => {
                            const newValue = statusFilter.includes(status.value)
                              ? statusFilter.filter(s => s !== status.value)
                              : [...statusFilter, status.value];
                            handleStatusFilterChange(newValue);
                          }}
                          className="justify-between"
                        >
                          <span>{status.label}</span>
                          {statusFilter.includes(status.value) && (
                            <Check className="h-4 w-4" />
                          )}
                        </CommandItem>
                      ))}
                    </CommandGroup>
                    </Command>
                  </PopoverContent>
                </Popover>

                <Select value={priorityFilter} onValueChange={handlePriorityFilterChange}>
                  <SelectTrigger className="w-full h-9">
                    <SelectValue placeholder="Priority" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="All">All Priorities</SelectItem>
                    <SelectItem value="10">10 (Highest)</SelectItem>
                    <SelectItem value="9">9</SelectItem>
                    <SelectItem value="8">8</SelectItem>
                    <SelectItem value="7">7</SelectItem>
                    <SelectItem value="6">6</SelectItem>
                    <SelectItem value="5">5</SelectItem>
                    <SelectItem value="4">4</SelectItem>
                    <SelectItem value="3">3</SelectItem>
                    <SelectItem value="2">2</SelectItem>
                    <SelectItem value="1">1 (Lowest)</SelectItem>
                  </SelectContent>
                </Select>

                <Select value={categoryFilter} onValueChange={handleCategoryFilterChange}>
                  <SelectTrigger className="w-full h-9">
                    <SelectValue placeholder="Category" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="All">All Categories</SelectItem>
                    {activeCategories.map((category) => (
                      <SelectItem key={category} value={category}>
                        {category}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>

                <DatePickerWithRange
                  buttonClassName="h-9 text-black dark:text-white"
                  dateRange={dateRange}
                  onDateRangeChange={handleDateRangeChange}
                />
              </FilterDropdown>
            </div>
            <div className="hidden xl:flex flex-wrap items-center gap-2">
              <Popover>
                <PopoverTrigger asChild>
                  <Button variant="outline" className="w-[130px] 2xl:w-[180px] text-xs 2xl:text-sm h-9 justify-between">
                    <span className="truncate font-normal">
                      {statusFilter.length === 0 
                        ? "All Statuses"
                        : statusFilter.join(", ")}
                    </span>
                    <ChevronDown className="ml-2 h-4 w-4 opacity-50" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-[180px] p-0" align="start">
                  <Command className="font-normal">
                    <CommandGroup>
                      <CommandItem
                        onSelect={() => handleStatusFilterChange([])}
                        className="justify-between"
                      >
                        <span>All Statuses</span>
                        {statusFilter.length === 0 && <Check className="h-4 w-4" />}
                      </CommandItem>
                      {statuses.map((status) => (
                        <CommandItem
                          key={status.value}
                          onSelect={() => {
                            const newValue = statusFilter.includes(status.value)
                              ? statusFilter.filter(s => s !== status.value)
                              : [...statusFilter, status.value];
                            handleStatusFilterChange(newValue);
                          }}
                          className="justify-between"
                        >
                          <span>{status.label}</span>
                          {statusFilter.includes(status.value) && (
                            <Check className="h-4 w-4" />
                          )}
                        </CommandItem>
                      ))}
                    </CommandGroup>
                  </Command>
                </PopoverContent>
              </Popover>

              <Select value={priorityFilter} onValueChange={handlePriorityFilterChange}>
                <SelectTrigger className="w-[130px] 2xl:w-[180px] text-xs 2xl:text-sm h-9 focus:ring-0">
                  <SelectValue placeholder="Priority" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="All">All Priorities</SelectItem>
                  <SelectItem value="10">10 (Highest)</SelectItem>
                  <SelectItem value="9">9</SelectItem>
                  <SelectItem value="8">8</SelectItem>
                  <SelectItem value="7">7</SelectItem>
                  <SelectItem value="6">6</SelectItem>
                  <SelectItem value="5">5</SelectItem>
                  <SelectItem value="4">4</SelectItem>
                  <SelectItem value="3">3</SelectItem>
                  <SelectItem value="2">2</SelectItem>
                  <SelectItem value="1">1 (Lowest)</SelectItem>
                </SelectContent>
              </Select>

              <Select value={categoryFilter} onValueChange={handleCategoryFilterChange}>
                <SelectTrigger className="w-[130px] 2xl:w-[180px] text-xs 2xl:text-sm h-9 focus:ring-0">
                  <SelectValue placeholder="Category" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="All">All Categories</SelectItem>
                  {activeCategories.map((category) => (
                    <SelectItem key={category} value={category}>
                      {category}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <DatePickerWithRange
                buttonClassName="h-9 text-black dark:text-white text-xs 2xl:text-sm"
                dateRange={dateRange}
                onDateRangeChange={handleDateRangeChange}
              />

              {getActiveFilterCount() > 0 && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={resetFilters}
                  className="h-9 text-xs 2xl:text-sm border dark:border-gray-800"
                >
                  <X className="mr-1 h-4 w-4" />
                  Reset
                </Button>
              )}
           
            </div>
          </>
        )}
      </div>

      <div className="text-sm text-muted-foreground flex items-center justify-between">
        <span>{getTotalTasksCount()} task{getTotalTasksCount() !== 1 ? "s" : ""} found</span>
        <div className="flex items-center gap-2">
          <span className="text-xs text-muted-foreground">
            Last updated: {lastRefreshTime.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })}
          </span>          
          <div className="flex">
            <Button
              size="sm"
              variant="outline"
              className="h-8 rounded-r-none border-r-0"
              disabled={isRefreshing}
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                handleManualRefresh();
              }}
            >
              <RefreshCw className={`h-4 w-4 ${isRefreshing ? 'animate-spin' : ''}`} />
            </Button>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  size="sm"
                  variant="outline"
                  className="h-8 rounded-l-none px-2"
                  disabled={isRefreshing}
                >
                  <ChevronDown className="h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" onCloseAutoFocus={(e) => e.preventDefault()}>
                <DropdownMenuItem onClick={(e) => {
                  e.preventDefault();
                  handleAutoRefreshToggle();
                }}>
                  <div className="flex items-center justify-between w-full">
                    <span>Auto-refresh</span>
                    <div className={`w-5 h-3 rounded-full transition-colors ${autoRefreshEnabled ? 'bg-green-500' : 'bg-gray-300'}`}>
                      <div className={`w-3 h-3 rounded-full bg-white shadow-sm transform transition-transform ${autoRefreshEnabled ? 'translate-x-2' : 'translate-x-0'}`} />
                    </div>
                  </div>
                </DropdownMenuItem>
                {autoRefreshEnabled && (
                  <>
                    <DropdownMenuSeparator />
                    <DropdownMenuLabel>Interval</DropdownMenuLabel>
                    <DropdownMenuItem onClick={(e) => {
                      e.preventDefault();
                      handleIntervalSelection(5);
                    }}>
                      <div className="flex items-center justify-between w-full">
                        <span>5 minutes</span>
                        {lastSelectedInterval === 5 && <Check className="h-4 w-4" />}
                      </div>
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={(e) => {
                      e.preventDefault();
                      handleIntervalSelection(10);
                    }}>
                      <div className="flex items-center justify-between w-full">
                        <span>10 minutes</span>
                        {lastSelectedInterval === 10 && <Check className="h-4 w-4" />}
                      </div>
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={(e) => {
                      e.preventDefault();
                      handleIntervalSelection(15);
                    }}>
                      <div className="flex items-center justify-between w-full">
                        <span>15 minutes</span>
                        {lastSelectedInterval === 15 && <Check className="h-4 w-4" />}
                      </div>
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={(e) => {
                      e.preventDefault();
                      handleIntervalSelection(30);
                    }}>
                      <div className="flex items-center justify-between w-full">
                        <span>30 minutes</span>
                        {lastSelectedInterval === 30 && <Check className="h-4 w-4" />}
                      </div>
                    </DropdownMenuItem>
                  </>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
          {/* View Type Toggle */}
          <div className="flex items-center gap-1 border rounded-md p-1">
            <Button
              variant={!isCategorizedView ? "default" : "ghost"}
              size="sm"
              className={`h-7 w-7 p-0 ${!isCategorizedView && "dark:text-white"}`}
              onClick={() => !isCategorizedView || toggleCategorizedView()}
              title="Flat list view"
            >
              <List className="h-4 w-4" />
            </Button>
            <Button
              variant={isCategorizedView ? "default" : "ghost"}
              size="sm"
              className={`h-7 w-7 p-0 ${isCategorizedView && "dark:text-white"}`}
              onClick={() => isCategorizedView || toggleCategorizedView()}
              title="Categorized view"
            >
              <Grid3X3 className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </div>

      <Card className={cn(
         isAtBottom && selectedTasks.length > 0 ? 'mb-14' : ''
       )}>
        <CardContent className="p-0">
          {/* Refresh indicator */}
          {isRefreshing && (
            <div className="flex items-center justify-center gap-2 py-2 bg-blue-50 dark:bg-blue-900/20 border-b">
              <RefreshCw className="h-4 w-4 animate-spin text-blue-600" />
              <span className="text-sm text-blue-600 dark:text-blue-400">
                Refreshing tasks...
              </span>
            </div>
          )}
          {isCategorizedView ? (
            <>
              {/* Table Header - rendered once at top for category view */}
              <div className="rounded-md border-b">
                <div className="min-w-full">
                  <Table className="w-full">
                    <TableHeader>
                  <TableRow>
                    <TableHead className="w-[50px]">
                      <div className="flex items-center justify-center">
                        <input
                          type="checkbox"
                          className="h-4 w-4 rounded border-gray-300 text-primary focus:ring-primary"
                          checked={(() => {
                            const taskItems = isCategorizedView 
                              ? sortedTasks.filter(item => item.type === 'task')
                              : filteredTasks;
                            return selectedTasks.length === taskItems.length && taskItems.length > 0;
                          })()}
                          onChange={toggleAllTasks}
                        />
                      </div>
                    </TableHead>
                    <TableHead
                      className="table-cell cursor-pointer hover:bg-muted/50 transition-colors"
                      onClick={(e) => handleSort("id", e)}
                    >
                      <div className={cn(
                        "flex items-center",
                        filteredTasks.some(task => task.isParent && task.subtasks && task.subtasks.length > 0)
                          ? "justify-between"
                          : "justify-between"
                      )}>
                        {filteredTasks.some(task => task.isParent && task.subtasks && task.subtasks.length > 0) && (
                          <div className="flex items-center w-6"></div>
                        )}
                        <div className={cn(
                          "flex items-center",
                          filteredTasks.some(task => task.isParent && task.subtasks && task.subtasks.length > 0)
                            ? "flex-1"
                            : "justify-between"
                        )}>
                          <span>ID</span>
                          {renderSortIndicator("id")}
                        </div>
                      </div>
                    </TableHead>
                    <TableHead
                      className="cursor-pointer w-[200px] hover:bg-muted/50 transition-colors"
                      onClick={(e) => handleSort("name", e)}
                    >
                      <div className="flex items-center justify-between">
                        <span>Task</span>
                        {renderSortIndicator("name")}
                      </div>
                    </TableHead>
                    <TableHead
                      className="cursor-pointer hover:bg-muted/50 transition-colors"
                      onClick={(e) => handleSort("status", e)}
                    >
                      <div className="flex items-center justify-between">
                        <span>Status</span>
                        {renderSortIndicator("status")}
                      </div>
                    </TableHead>
                    <TableHead
                      className="table-cell cursor-pointer hover:bg-muted/50 transition-colors"
                      onClick={(e) => handleSort("priority", e)}
                    >
                      <div className="flex items-center justify-between">
                        <span>Priority</span>
                        {renderSortIndicator("priority")}
                      </div>
                    </TableHead>
                    <TableHead
                      className="cursor-pointer hover:bg-muted/50 transition-colors"
                      onClick={(e) => handleSort("assignee", e)}
                    >
                      <div className="flex items-center justify-between">
                        <span>Assignee</span>
                        {renderSortIndicator("assignee")}
                      </div>
                    </TableHead>
                    <TableHead
                      className="cursor-pointer hover:bg-muted/50 transition-colors"
                      onClick={(e) => handleSort("dueDate", e)}
                    >
                      <div className="flex items-center justify-between">
                        <span>Due Date</span>
                        {renderSortIndicator("dueDate")}
                      </div>
                    </TableHead>
                    <TableHead
                      className="table-cell cursor-pointer hover:bg-muted/50 transition-colors"
                      onClick={(e) => handleSort("category", e)}
                    >
                      <div className="flex items-center justify-between">
                        <span>Category</span>
                        {renderSortIndicator("category")}
                      </div>
                    </TableHead>
                    <TableHead className="w-[50px]"></TableHead>
                  </TableRow>
                </TableHeader>
                  </Table>
                </div>
              </div>
              {/* Category groups - each with its own scrollable container */}
              {sortedTasks.length === 0 ? (
                <div className="rounded-md border-t p-6 text-center">
                  <div className="flex flex-col items-center gap-2">
                    <Search className="h-8 w-8 text-muted-foreground" />
                    <h3 className="text-lg font-semibold">
                      No tasks found
                    </h3>
                    <p className="text-sm text-muted-foreground">
                      Try adjusting your search or filter to find what
                      you're looking for.
                    </p>
                  </div>
                </div>
              ) : (
                <DndContext
                  sensors={sensors}
                  collisionDetection={closestCenter}
                  onDragEnd={handleCategoryDragEnd}
                >
                  <SortableContext
                    items={sortedTasks
                      .filter(item => item.type === 'category')
                      .map(item => item.category)}
                    strategy={verticalListSortingStrategy}
                  >
                    {(() => {
                      // Group items by category for rendering with pagination controls
                      const categoryGroups = [];
                      let currentCategory = null;
                      let currentCategoryItems = [];
                      
                      paginatedTasks.forEach((item, idx) => {
                        if (item.type === 'category') {
                          // Save previous category group if exists
                          if (currentCategory !== null) {
                            categoryGroups.push({
                              category: currentCategory,
                              items: currentCategoryItems
                            });
                          }
                          // Start new category
                          currentCategory = item.category;
                          currentCategoryItems = [item];
                        } else {
                          // Add task to current category
                          currentCategoryItems.push(item);
                        }
                      });
                      
                      // Add last category group
                      if (currentCategory !== null) {
                        categoryGroups.push({
                          category: currentCategory,
                          items: currentCategoryItems
                        });
                      }
                      
                      // Render each category group
                      return categoryGroups.map((group, groupIdx) => {
                        const category = group.category;
                        const categoryHeader = group.items.find(item => item.type === 'category');
                        const categoryTaskItems = group.items.filter(item => item.type === 'task');
                        const isLoading = loadingCategories.has(category);
                        const pagination = categoryPagination[category];
                        const isExpanded = categoryHeader?.isExpanded || false;
                        
                        return (
                          <React.Fragment key={`category-group-${category}`}>
                            {/* Category header - outside scrollable */}
                            <div className="border-b">
                              <Table>
                                <TableBody>
                                  <SortableCategoryRow
                                    category={category}
                                    taskCount={categoryHeader?.taskCount || 0}
                                    isExpanded={isExpanded}
                                    onToggle={toggleCategoryExpansion}
                                    isLoading={isLoading}
                                  />
                                </TableBody>
                              </Table>
                            </div>
                            
                            {/* Scrollable table container for this category */}
                            {isExpanded && categoryTaskItems.length > 0 && (
                              <div className="overflow-x-auto border-b">
                                <div className="min-w-full">
                                  <Table className="w-full">
                                    <TableBody>
                                      {categoryTaskItems.map((item) => {
                                const task = item;
                        const isOverdue =
                          isTaskOverdue(task) &&
                          task.status !== "Completed";
                        
                        // Handle filtered subtasks (standalone subtasks that match filters)
                        const isFilteredSubtask = task.isFilteredSubtask;
                        
                        return (
                          <React.Fragment key={task.id}>
                            <TableRow
                              className={cn(
                                "cursor-pointer",
                                isOverdue &&
                                  "bg-red-50 dark:bg-red-900/50 hover:bg-red-100 dark:hover:bg-red-900/35 pulse",
                                isFilteredSubtask && "bg-muted/20", // Subtle background for filtered subtasks
                                selectedTasks.includes(task.id) 
                                  ? "bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-800 hover:bg-blue-100 dark:hover:bg-blue-900/30" 
                                  : isOverdue
                                    ? "hover:bg-red-100 dark:hover:bg-red-900/35"
                                    : "hover:bg-muted/50"
                              )}
                              onClick={() => handleOpenTaskDetail(task)}
                            >
                              <TableCell className="w-[50px]">
                                <div 
                                  className="flex items-center justify-center"
                                  onClick={(e) => e.stopPropagation()}
                                >
                                  <input
                                    type="checkbox"
                                    className="h-4 w-4 rounded border-gray-300 text-primary focus:ring-primary"
                                    checked={selectedTasks.includes(task.id)}
                                    onChange={(e) => {
                                      e.stopPropagation();
                                      toggleTaskSelection(task.id);
                                    }}
                                    onClick={(e) => {
                                      e.stopPropagation();
                                    }}
                                  />
                                </div>
                              </TableCell>
                              <TableCell className="table-cell font-medium">
                                <div className={cn(
                                  "flex items-center",
                                  filteredTasks.some(task => task.isParent && task.subtasks && task.subtasks.length > 0)
                                    ? "justify-between"
                                    : "justify-between"
                                )}>
                                  {filteredTasks.some(task => task.isParent && task.subtasks && task.subtasks.length > 0) && (
                                    <div className="flex items-center w-6">
                                      {task.isParent &&
                                        task.subtasks &&
                                        task.subtasks.length > 0 && (
                                          <Button
                                            variant="ghost"
                                            size="icon"
                                            className="h-6 w-6"
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              toggleExpandTask(task.id);
                                            }}
                                          >
                                            {expandedTasks.includes(task.id) ? (
                                              <ChevronDown className="h-4 w-4" />
                                            ) : (
                                              <ChevronRightIcon className="h-4 w-4" />
                                            )}
                                          </Button>
                                        )}
                                    </div>
                                  )}
                                  <div className={cn(
                                    "flex items-center",
                                    filteredTasks.some(task => task.isParent && task.subtasks && task.subtasks.length > 0)
                                      ? "flex-1 justify-end"
                                      : "justify-between"
                                  )}>
                                    {task.id}
                                  </div>
                                </div>
                              </TableCell>
                              <TableCell>
                                <div>
                                  <div className="font-medium flex items-center">
                                    {task.name}
                                    {!task.is_acknowledged && (
                                      <Badge
                                        variant="outline"
                                        className="ml-2 bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-400"
                                      >
                                        New
                                      </Badge>
                                    )}
                                    {task.isParent && task.subtasks && task.subtasks.length > 0 && (
                                      <Badge
                                        variant="outline"
                                        className="ml-2 bg-primary/10 text-primary"
                                      >
                                        Parent
                                      </Badge>
                                    )}
                                    {isFilteredSubtask && (
                                      <Badge
                                        variant="outline"
                                        className="ml-2 bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400"
                                      >
                                        Subtask
                                      </Badge>
                                    )}
                                    {/* Progress Bar for Parent Tasks */}
                                    {task.isParent && task.subtasks && task.subtasks.length > 0 && (
                                      <div className="flex items-center gap-2 mt-1 ml-2">
                                        <div className="w-16 h-1 bg-gray-200 rounded-full overflow-hidden">
                                          <div 
                                            className="h-full bg-green-500 transition-all duration-300"
                                            style={{
                                              width: `${(task.subtasks.filter(subtask => subtask.status === "Completed").length / task.subtasks.length) * 100}%`
                                            }}
                                          />
                                        </div>
                                        <span className="text-xs text-muted-foreground">
                                          {task.subtasks.filter(subtask => subtask.status === "Completed").length}/{task.subtasks.length}
                                        </span>
                                      </div>
                                    )}

                                    {isOverdue && (
                                      <Badge
                                        variant="outline"
                                        className="ml-2 bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400"
                                      >
                                        <AlertTriangle className="h-3 w-3 mr-1" />
                                        Overdue
                                      </Badge>
                                    )}
                                  </div>
                                  <div className="text-sm text-muted-foreground truncate max-w-[150px] sm:max-w-[200px] md:max-w-[300px]">
                                    <div className="truncate">{task.description}</div>
                                    {isFilteredSubtask && task.originalParentName && (
                                      <div className="text-xs text-blue-600 dark:text-blue-400 mt-1">
                                        Parent: {task.originalParentName}
                                      </div>
                                    )}
                                  </div>
                                  {/* Notes and Checklist Indicators */}
                                  <div className="flex items-center gap-2 mt-1">
                                    {task.notes && task.notes.length > 0 && (
                                      <div className="flex items-center gap-1">
                                        <MessageSquare className="h-3 w-3 text-muted-foreground" />
                                        <span className="text-xs text-muted-foreground">
                                          {task.notes.length}
                                        </span>
                                      </div>
                                    )}
                                    {task.checklist && task.checklist.length > 0 && (
                                      <ChecklistIndicator 
                                        checklist={task.checklist} 
                                        size="sm"
                                        showProgressBar={true}
                                      />
                                    )}
                                  </div>
                                </div>
                              </TableCell>
                              <TableCell className="w-auto min-w-[150px] max-w-[200px]">
                                <Badge
                                  variant="outline"
                                  className={cn(
                                    getStatusColor(task.status),
                                    "justify-center"
                                  )}
                                >
                                  {task.status}
                                </Badge>
                              </TableCell>
                              <TableCell className="table-cell">
                                <Badge
                                  variant="outline"
                                  className={getPriorityColor(task.priority)}
                                >
                                  {task.priority}
                                </Badge>
                              </TableCell>
                              <TableCell className="w-auto min-w-[120px] max-w-[200px]">
                                <StackedAssigneeAvatars
                                  users={task.users}
                                  size="md"
                                  showNameForSingle
                                />
                              </TableCell>
                              <TableCell className="w-auto min-w-[200px] max-w-[250px]">
                                {task.defer_date ? (
                                  <div className="flex items-center">
                                    <span>{formatTaskDate(task.defer_date)}</span>
                                    <span className="ml-2 text-xs text-muted-foreground">(Deferred)</span>
                                  </div>
                                ) : (
                                  formatTaskDate(task.due)
                                )}
                              </TableCell>
                              <TableCell className="w-auto min-w-[150px] max-w-[200px]">
                                {task.category}
                              </TableCell>
                              <TableCell onClick={(e) => e.stopPropagation()}>
                                <DropdownMenu>
                                  <DropdownMenuTrigger asChild>
                                    <Button
                                      variant="ghost"
                                      className="h-8 w-8 p-0"
                                    >
                                      <span className="sr-only">Open menu</span>
                                      <MoreVertical className="h-4 w-4" />
                                    </Button>
                                  </DropdownMenuTrigger>
                                  <DropdownMenuContent align="end">
                                    <DropdownMenuLabel>
                                      Actions
                                    </DropdownMenuLabel>
                                    <DropdownMenuItem
                                      className="text-red-600"
                                      onClick={() => handleDeleteTask(task.id)}
                                    >
                                      <Trash2 className="mr-2 h-4 w-4" />
                                      Delete
                                    </DropdownMenuItem>
                                  </DropdownMenuContent>
                                </DropdownMenu>
                              </TableCell>
                            </TableRow>

                            {/* Subtasks */}
                            {task.isParent &&
                              task.subtasks &&
                              task.subtasks.length > 0 &&
                              expandedTasks.includes(task.id) &&
                              task.subtasks
                                .filter(subtask => !subtask.isFilteredSubtask) // Don't show filtered subtasks as nested
                                .map((subtask) => {
                                const isSubtaskOverdue =
                                  isTaskOverdue(subtask) &&
                                  subtask.status !== "Completed";
                                return (
                                  <TableRow
                                    key={subtask.id}
                                    className={cn(
                                      "cursor-pointer bg-muted/20",
                                      isSubtaskOverdue &&
                                        "bg-red-50 dark:bg-red-900/10 pulse",
                                      selectedTasks.includes(subtask.id) 
                                        ? "bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-800 hover:bg-blue-100 dark:hover:bg-blue-900/30" 
                                        : isSubtaskOverdue
                                          ? "hover:bg-red-100 dark:hover:bg-red-900/20"
                                          : "hover:bg-muted/50"
                                    )}
                                    onClick={() =>
                                      handleOpenTaskDetail(subtask)
                                    }
                                  >
                                    <TableCell className="w-[50px]">
                                      <div 
                                        className="flex items-center justify-center"
                                        onClick={(e) => e.stopPropagation()}
                                      >
                                        <input
                                          type="checkbox"
                                          className="h-4 w-4 rounded border-gray-300 text-primary focus:ring-primary"
                                          checked={selectedTasks.includes(subtask.id)}
                                          onChange={(e) => {
                                            e.stopPropagation();
                                            toggleTaskSelection(subtask.id);
                                          }}
                                          onClick={(e) => {
                                            e.stopPropagation();
                                          }}
                                        />
                                      </div>
                                    </TableCell>
                                    <TableCell className="table-cell font-medium">
                                      <div className={cn(
                                        "flex items-center",
                                        filteredTasks.some(task => task.isParent && task.subtasks && task.subtasks.length > 0)
                                          ? "justify-between"
                                          : "justify-between"
                                      )}>
                                        {filteredTasks.some(task => task.isParent && task.subtasks && task.subtasks.length > 0) && (
                                          <div className="flex items-center w-6">
                                            <div className="w-6"></div>
                                          </div>
                                        )}
                                        <div className={cn(
                                          "flex items-center",
                                          filteredTasks.some(task => task.isParent && task.subtasks && task.subtasks.length > 0)
                                            ? "flex-1 justify-end"
                                            : "justify-between"
                                        )}>
                                          {subtask.id}
                                        </div>
                                      </div>
                                    </TableCell>
                                    <TableCell>
                                      <div>
                                        <div className="font-medium flex items-center flex-wrap gap-1">
                                          <div className="border-l-2 border-primary/50 pl-2 flex-1 min-w-[100px]">
                                            {subtask.name}
                                          </div>

                                          {!subtask.is_acknowledged && (
                                            <Badge
                                              variant="outline"
                                              className="ml-2 bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-400"
                                            >
                                              New
                                            </Badge>
                                          )}
                                          {isSubtaskOverdue && (
                                            <Badge
                                              variant="outline"
                                              className="ml-2 bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400"
                                            >
                                              <AlertTriangle className="h-3 w-3 mr-1" />
                                              Overdue
                                            </Badge>
                                          )}
                                        </div>
                                        <div className="text-sm text-muted-foreground truncate max-w-[150px] sm:max-w-[200px] md:max-w-[300px] pl-2">
                                          <div className="truncate">{subtask.description}</div>
                                        </div>
                                        {/* Notes and Checklist Indicators */}
                                        <div className="flex items-center gap-2 pl-2 mt-1">
                                          <div className="text-xs text-muted-foreground">
                                            Parent:{" "}
                                            {subtask.parentName ||
                                              subtask.parent_id}
                                          </div>
                                          {subtask.notes && subtask.notes.length > 0 && (
                                            <div className="flex items-center gap-1">
                                              <MessageSquare className="h-3 w-3 text-muted-foreground" />
                                              <span className="text-xs text-muted-foreground">
                                                {subtask.notes.length}
                                              </span>
                                            </div>
                                          )}
                                          {subtask.checklist && subtask.checklist.length > 0 && (
                                            <ChecklistIndicator 
                                              checklist={subtask.checklist} 
                                              size="sm"
                                              showProgressBar={true}
                                            />
                                          )}
                                        </div>
                                      </div>
                                    </TableCell>
                                    <TableCell className="w-[200px] min-w-[200px] max-w-[200px]">
                                      {(() => {
                                        return (
                                          <Badge
                                            variant="outline"
                                            className={cn(
                                              getStatusColor(subtask.status),
                                              "justify-center"
                                            )}
                                          >
                                            {subtask.status}
                                          </Badge>
                                        );
                                      })()}
                                    </TableCell>
                                    <TableCell className="table-cell">
                                      <Badge
                                        variant="outline"
                                        className={getPriorityColor(
                                          subtask.priority
                                        )}
                                      >
                                        {subtask.priority}
                                      </Badge>
                                    </TableCell>
                                    <TableCell className="w-[200px] min-w-[120px] max-w-[200px]">
                                      <StackedAssigneeAvatars
                                        users={subtask.users}
                                        size="md"
                                        showNameForSingle
                                      />
                                    </TableCell>
                                    <TableCell className="w-[200px] min-w-[200px] max-w-[200px]">
                                      {subtask.defer_date ? (
                                        <div className="flex items-center">
                                          <span>{formatTaskDate(subtask.defer_date)}</span>
                                          <span className="ml-2 text-xs text-muted-foreground">(Deferred)</span>
                                        </div>
                                      ) : (
                                        formatTaskDate(subtask.defer_date || subtask.dueDate || subtask.due)
                                      )}
                                    </TableCell>
                                    <TableCell className="table-cell">
                                      {subtask.category}
                                    </TableCell>
                                    <TableCell
                                      onClick={(e) => e.stopPropagation()}
                                    >
                                      <DropdownMenu>
                                        <DropdownMenuTrigger asChild>
                                          <Button
                                            variant="ghost"
                                            className="h-8 w-8 p-0"
                                          >
                                            <span className="sr-only">
                                              Open menu
                                            </span>
                                            <MoreVertical className="h-4 w-4" />
                                          </Button>
                                        </DropdownMenuTrigger>
                                        <DropdownMenuContent align="end">
                                          <DropdownMenuLabel>
                                            Actions
                                          </DropdownMenuLabel>
                                          <DropdownMenuItem
                                            className="text-red-600"
                                            onClick={() =>
                                              handleDeleteTask(subtask.id)
                                            }
                                          >
                                            <Trash2 className="mr-2 h-4 w-4" />
                                            Delete
                                          </DropdownMenuItem>
                                        </DropdownMenuContent>
                                      </DropdownMenu>
                                    </TableCell>
                                  </TableRow>
                                );
                              })}
                            </React.Fragment>
                          );
                        })}
                                    </TableBody>
                                  </Table>
                                </div>
                              </div>
                            )}
                            
                            {/* Category pagination - outside scrollable, inside category section */}
                            {isExpanded && pagination && pagination.total > pagination.perPage && (
                              <CategoryPaginationControls
                                category={category}
                                pagination={pagination}
                                onPageChange={(cat, newPage) => {
                                  fetchCategoryTasks(cat, newPage);
                                }}
                              />
                            )}
                          </React.Fragment>
                        );
                      });
                    })()}
                  </SortableContext>
                </DndContext>
              )}
              
              {/* Footer showing total tasks and categories for category view */}
              <div className="flex items-center justify-between p-4 border-t">
                <div className="text-sm text-muted-foreground">
                  <strong>{getTotalTasksCount()}</strong> total tasks across{" "}
                  <strong>{Object.keys(props.categoryCounts || {}).length}</strong> categories
                </div>
              </div>
            </>
          ) : (
            /* Flat view - single scrollable container */
            <div className="rounded-md border overflow-x-auto">
              <div className="min-w-full">
                <Table className="w-full">
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-[50px]">
                        <div className="flex items-center justify-center">
                          <input
                            type="checkbox"
                            className="h-4 w-4 rounded border-gray-300 text-primary focus:ring-primary"
                            checked={(() => {
                              const taskItems = isCategorizedView 
                                ? sortedTasks.filter(item => item.type === 'task')
                                : filteredTasks;
                              return selectedTasks.length === taskItems.length && taskItems.length > 0;
                            })()}
                            onChange={toggleAllTasks}
                          />
                        </div>
                      </TableHead>
                      <TableHead
                        className="table-cell cursor-pointer hover:bg-muted/50 transition-colors"
                        onClick={(e) => handleSort("id", e)}
                      >
                        <div className={cn(
                          "flex items-center",
                          filteredTasks.some(task => task.isParent && task.subtasks && task.subtasks.length > 0)
                            ? "justify-between"
                            : "justify-between"
                        )}>
                          {filteredTasks.some(task => task.isParent && task.subtasks && task.subtasks.length > 0) && (
                            <div className="flex items-center w-6"></div>
                          )}
                          <div className={cn(
                            "flex items-center",
                            filteredTasks.some(task => task.isParent && task.subtasks && task.subtasks.length > 0)
                              ? "flex-1"
                              : "justify-between"
                          )}>
                            <span>ID</span>
                            {renderSortIndicator("id")}
                          </div>
                        </div>
                      </TableHead>
                      <TableHead
                        className="cursor-pointer w-[200px] hover:bg-muted/50 transition-colors"
                        onClick={(e) => handleSort("name", e)}
                      >
                        <div className="flex items-center justify-between">
                          <span>Task</span>
                          {renderSortIndicator("name")}
                        </div>
                      </TableHead>
                      <TableHead
                        className="cursor-pointer hover:bg-muted/50 transition-colors"
                        onClick={(e) => handleSort("status", e)}
                      >
                        <div className="flex items-center justify-between">
                          <span>Status</span>
                          {renderSortIndicator("status")}
                        </div>
                      </TableHead>
                      <TableHead
                        className="table-cell cursor-pointer hover:bg-muted/50 transition-colors"
                        onClick={(e) => handleSort("priority", e)}
                      >
                        <div className="flex items-center justify-between">
                          <span>Priority</span>
                          {renderSortIndicator("priority")}
                        </div>
                      </TableHead>
                      <TableHead
                        className="cursor-pointer hover:bg-muted/50 transition-colors"
                        onClick={(e) => handleSort("assignee", e)}
                      >
                        <div className="flex items-center justify-between">
                          <span>Assignee</span>
                          {renderSortIndicator("assignee")}
                        </div>
                      </TableHead>
                      <TableHead
                        className="cursor-pointer hover:bg-muted/50 transition-colors"
                        onClick={(e) => handleSort("dueDate", e)}
                      >
                        <div className="flex items-center justify-between">
                          <span>Due Date</span>
                          {renderSortIndicator("dueDate")}
                        </div>
                      </TableHead>
                      <TableHead
                        className="table-cell cursor-pointer hover:bg-muted/50 transition-colors"
                        onClick={(e) => handleSort("category", e)}
                      >
                        <div className="flex items-center justify-between">
                          <span>Category</span>
                          {renderSortIndicator("category")}
                        </div>
                      </TableHead>
                      <TableHead className="w-[50px]"></TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {sortedTasks.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={9} className="h-24 text-center">
                          <div className="flex flex-col items-center gap-2">
                            <Search className="h-8 w-8 text-muted-foreground" />
                            <h3 className="text-lg font-semibold">
                              No tasks found
                            </h3>
                            <p className="text-sm text-muted-foreground">
                              Try adjusting your search or filter to find what
                              you're looking for.
                            </p>
                          </div>
                        </TableCell>
                      </TableRow>
                    ) : (
                    <>
                      {paginatedTasks.map((item, index) => {
                        // Non-categorized view - tasks only
                        const task = item;
                        const isOverdue =
                          isTaskOverdue(task) &&
                          task.status !== "Completed";
                        
                        // Handle filtered subtasks (standalone subtasks that match filters)
                        const isFilteredSubtask = task.isFilteredSubtask;
                        
                        return (
                          <React.Fragment key={task.id}>
                            <TableRow
                              className={cn(
                                "cursor-pointer",
                                isOverdue &&
                                  "bg-red-50 dark:bg-red-900/50 hover:bg-red-100 dark:hover:bg-red-900/35 pulse",
                                isFilteredSubtask && "bg-muted/20",
                                selectedTasks.includes(task.id) 
                                  ? "bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-800 hover:bg-blue-100 dark:hover:bg-blue-900/30" 
                                  : isOverdue
                                    ? "hover:bg-red-100 dark:hover:bg-red-900/35"
                                    : "hover:bg-muted/50"
                              )}
                              onClick={() => handleOpenTaskDetail(task)}
                            >
                              <TableCell className="w-[50px]">
                                <div 
                                  className="flex items-center justify-center"
                                  onClick={(e) => e.stopPropagation()}
                                >
                                  <input
                                    type="checkbox"
                                    className="h-4 w-4 rounded border-gray-300 text-primary focus:ring-primary"
                                    checked={selectedTasks.includes(task.id)}
                                    onChange={(e) => {
                                      e.stopPropagation();
                                      toggleTaskSelection(task.id);
                                    }}
                                    onClick={(e) => {
                                      e.stopPropagation();
                                    }}
                                  />
                                </div>
                              </TableCell>
                              <TableCell className="table-cell font-medium">
                                <div className={cn(
                                  "flex items-center",
                                  filteredTasks.some(task => task.isParent && task.subtasks && task.subtasks.length > 0)
                                    ? "justify-between"
                                    : "justify-between"
                                )}>
                                  {filteredTasks.some(task => task.isParent && task.subtasks && task.subtasks.length > 0) && (
                                    <div className="flex items-center w-6">
                                      {task.isParent &&
                                        task.subtasks &&
                                        task.subtasks.length > 0 && (
                                          <Button
                                            variant="ghost"
                                            size="icon"
                                            className="h-6 w-6"
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              toggleExpandTask(task.id);
                                            }}
                                          >
                                            {expandedTasks.includes(task.id) ? (
                                              <ChevronDown className="h-4 w-4" />
                                            ) : (
                                              <ChevronRightIcon className="h-4 w-4" />
                                            )}
                                          </Button>
                                        )}
                                    </div>
                                  )}
                                  <div className={cn(
                                    "flex items-center",
                                    filteredTasks.some(task => task.isParent && task.subtasks && task.subtasks.length > 0)
                                      ? "flex-1 justify-end"
                                      : "justify-between"
                                  )}>
                                    {task.id}
                                  </div>
                                </div>
                              </TableCell>
                              <TableCell>
                                <div>
                                  <div className="font-medium flex items-center">
                                    {task.name}
                                    {!task.is_acknowledged && (
                                      <Badge
                                        variant="outline"
                                        className="ml-2 bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-400"
                                      >
                                        New
                                      </Badge>
                                    )}
                                    {task.isParent && task.subtasks && task.subtasks.length > 0 && (
                                      <Badge
                                        variant="outline"
                                        className="ml-2 bg-primary/10 text-primary"
                                      >
                                        Parent
                                      </Badge>
                                    )}
                                    {isFilteredSubtask && (
                                      <Badge
                                        variant="outline"
                                        className="ml-2 bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400"
                                      >
                                        Subtask
                                      </Badge>
                                    )}
                                    {task.isParent && task.subtasks && task.subtasks.length > 0 && (
                                      <div className="flex items-center gap-2 mt-1 ml-2">
                                        <div className="w-16 h-1 bg-gray-200 rounded-full overflow-hidden">
                                          <div 
                                            className="h-full bg-green-500 transition-all duration-300"
                                            style={{
                                              width: `${(task.subtasks.filter(subtask => subtask.status === "Completed").length / task.subtasks.length) * 100}%`
                                            }}
                                          />
                                        </div>
                                        <span className="text-xs text-muted-foreground">
                                          {task.subtasks.filter(subtask => subtask.status === "Completed").length}/{task.subtasks.length}
                                        </span>
                                      </div>
                                    )}
                                    {isOverdue && (
                                      <Badge
                                        variant="outline"
                                        className="ml-2 bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400"
                                      >
                                        <AlertTriangle className="h-3 w-3 mr-1" />
                                        Overdue
                                      </Badge>
                                    )}
                                  </div>
                                  <div className="text-sm text-muted-foreground truncate max-w-[150px] sm:max-w-[200px] md:max-w-[300px]">
                                    <div className="truncate">{task.description}</div>
                                    {isFilteredSubtask && task.originalParentName && (
                                      <div className="text-xs text-blue-600 dark:text-blue-400 mt-1">
                                        Parent: {task.originalParentName}
                                      </div>
                                    )}
                                  </div>
                                  <div className="flex items-center gap-2 mt-1">
                                    {task.notes && task.notes.length > 0 && (
                                      <div className="flex items-center gap-1">
                                        <MessageSquare className="h-3 w-3 text-muted-foreground" />
                                        <span className="text-xs text-muted-foreground">
                                          {task.notes.length}
                                        </span>
                                      </div>
                                    )}
                                    {task.checklist && task.checklist.length > 0 && (
                                      <ChecklistIndicator 
                                        checklist={task.checklist} 
                                        size="sm"
                                        showProgressBar={true}
                                      />
                                    )}
                                  </div>
                                </div>
                              </TableCell>
                              <TableCell className="w-auto min-w-[150px] max-w-[200px]">
                                <Badge
                                  variant="outline"
                                  className={cn(
                                    getStatusColor(task.status),
                                    "justify-center"
                                  )}
                                >
                                  {task.status}
                                </Badge>
                              </TableCell>
                              <TableCell className="table-cell">
                                <Badge
                                  variant="outline"
                                  className={getPriorityColor(task.priority)}
                                >
                                  {task.priority}
                                </Badge>
                              </TableCell>
                              <TableCell className="w-auto min-w-[120px] max-w-[200px]">
                                <StackedAssigneeAvatars
                                  users={task.users}
                                  size="md"
                                  showNameForSingle
                                />
                              </TableCell>
                              <TableCell className="w-auto min-w-[200px] max-w-[250px]">
                                {task.defer_date ? (
                                  <div className="flex items-center">
                                    <span>{formatTaskDate(task.defer_date)}</span>
                                    <span className="ml-2 text-xs text-muted-foreground">(Deferred)</span>
                                  </div>
                                ) : (
                                  formatTaskDate(task.due)
                                )}
                              </TableCell>
                              <TableCell className="w-auto min-w-[150px] max-w-[200px]">
                                {task.category}
                              </TableCell>
                              <TableCell onClick={(e) => e.stopPropagation()}>
                                <DropdownMenu>
                                  <DropdownMenuTrigger asChild>
                                    <Button
                                      variant="ghost"
                                      className="h-8 w-8 p-0"
                                    >
                                      <span className="sr-only">Open menu</span>
                                      <MoreVertical className="h-4 w-4" />
                                    </Button>
                                  </DropdownMenuTrigger>
                                  <DropdownMenuContent align="end">
                                    <DropdownMenuLabel>
                                      Actions
                                    </DropdownMenuLabel>
                                    <DropdownMenuItem
                                      className="text-red-600"
                                      onClick={() => handleDeleteTask(task.id)}
                                    >
                                      <Trash2 className="mr-2 h-4 w-4" />
                                      Delete
                                    </DropdownMenuItem>
                                  </DropdownMenuContent>
                                </DropdownMenu>
                              </TableCell>
                            </TableRow>

                            {/* Subtasks */}
                            {task.isParent &&
                              task.subtasks &&
                              task.subtasks.length > 0 &&
                              expandedTasks.includes(task.id) &&
                              task.subtasks
                                .filter(subtask => !subtask.isFilteredSubtask)
                                .map((subtask) => {
                                const isSubtaskOverdue =
                                  isTaskOverdue(subtask) &&
                                  subtask.status !== "Completed";
                                return (
                                  <TableRow
                                    key={subtask.id}
                                    className={cn(
                                      "cursor-pointer bg-muted/20",
                                      isSubtaskOverdue &&
                                        "bg-red-50 dark:bg-red-900/10 pulse",
                                      selectedTasks.includes(subtask.id) 
                                        ? "bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-800 hover:bg-blue-100 dark:hover:bg-blue-900/30" 
                                        : isSubtaskOverdue
                                          ? "hover:bg-red-100 dark:hover:bg-red-900/20"
                                          : "hover:bg-muted/50"
                                    )}
                                    onClick={() =>
                                      handleOpenTaskDetail(subtask)
                                    }
                                  >
                                    <TableCell className="w-[50px]">
                                      <div 
                                        className="flex items-center justify-center"
                                        onClick={(e) => e.stopPropagation()}
                                      >
                                        <input
                                          type="checkbox"
                                          className="h-4 w-4 rounded border-gray-300 text-primary focus:ring-primary"
                                          checked={selectedTasks.includes(subtask.id)}
                                          onChange={(e) => {
                                            e.stopPropagation();
                                            toggleTaskSelection(subtask.id);
                                          }}
                                          onClick={(e) => {
                                            e.stopPropagation();
                                          }}
                                        />
                                      </div>
                                    </TableCell>
                                    <TableCell className="table-cell font-medium">
                                      <div className={cn(
                                        "flex items-center",
                                        filteredTasks.some(task => task.isParent && task.subtasks && task.subtasks.length > 0)
                                          ? "justify-between"
                                          : "justify-between"
                                      )}>
                                        {filteredTasks.some(task => task.isParent && task.subtasks && task.subtasks.length > 0) && (
                                          <div className="flex items-center w-6">
                                            <div className="w-6"></div>
                                          </div>
                                        )}
                                        <div className={cn(
                                          "flex items-center",
                                          filteredTasks.some(task => task.isParent && task.subtasks && task.subtasks.length > 0)
                                            ? "flex-1 justify-end"
                                            : "justify-between"
                                        )}>
                                          {subtask.id}
                                        </div>
                                      </div>
                                    </TableCell>
                                    <TableCell>
                                      <div>
                                        <div className="font-medium flex items-center flex-wrap gap-1">
                                          <div className="border-l-2 border-primary/50 pl-2 flex-1 min-w-[100px]">
                                            {subtask.name}
                                          </div>
                                          {!subtask.is_acknowledged && (
                                            <Badge
                                              variant="outline"
                                              className="ml-2 bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-400"
                                            >
                                              New
                                            </Badge>
                                          )}
                                          {isSubtaskOverdue && (
                                            <Badge
                                              variant="outline"
                                              className="ml-2 bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400"
                                            >
                                              <AlertTriangle className="h-3 w-3 mr-1" />
                                              Overdue
                                            </Badge>
                                          )}
                                        </div>
                                        <div className="text-sm text-muted-foreground truncate max-w-[150px] sm:max-w-[200px] md:max-w-[300px] pl-2">
                                          <div className="truncate">{subtask.description}</div>
                                        </div>
                                        <div className="flex items-center gap-2 pl-2 mt-1">
                                          <div className="text-xs text-muted-foreground">
                                            Parent:{" "}
                                            {subtask.parentName ||
                                              subtask.parent_id}
                                          </div>
                                          {subtask.notes && subtask.notes.length > 0 && (
                                            <div className="flex items-center gap-1">
                                              <MessageSquare className="h-3 w-3 text-muted-foreground" />
                                              <span className="text-xs text-muted-foreground">
                                                {subtask.notes.length}
                                              </span>
                                            </div>
                                          )}
                                          {subtask.checklist && subtask.checklist.length > 0 && (
                                            <ChecklistIndicator 
                                              checklist={subtask.checklist} 
                                              size="sm"
                                              showProgressBar={true}
                                            />
                                          )}
                                        </div>
                                      </div>
                                    </TableCell>
                                    <TableCell className="w-[200px] min-w-[200px] max-w-[200px]">
                                      <Badge
                                        variant="outline"
                                        className={cn(
                                          getStatusColor(subtask.status),
                                          "justify-center"
                                        )}
                                      >
                                        {subtask.status}
                                      </Badge>
                                    </TableCell>
                                    <TableCell className="table-cell">
                                      <Badge
                                        variant="outline"
                                        className={getPriorityColor(
                                          subtask.priority
                                        )}
                                      >
                                        {subtask.priority}
                                      </Badge>
                                    </TableCell>
                                    <TableCell className="w-[200px] min-w-[120px] max-w-[200px]">
                                      <StackedAssigneeAvatars
                                        users={subtask.users}
                                        size="md"
                                        showNameForSingle
                                      />
                                    </TableCell>
                                    <TableCell className="w-[200px] min-w-[200px] max-w-[200px]">
                                      {subtask.defer_date ? (
                                        <div className="flex items-center">
                                          <span>{formatTaskDate(subtask.defer_date)}</span>
                                          <span className="ml-2 text-xs text-muted-foreground">(Deferred)</span>
                                        </div>
                                      ) : (
                                        formatTaskDate(subtask.defer_date || subtask.dueDate || subtask.due)
                                      )}
                                    </TableCell>
                                    <TableCell className="table-cell">
                                      {subtask.category}
                                    </TableCell>
                                    <TableCell
                                      onClick={(e) => e.stopPropagation()}
                                    >
                                      <DropdownMenu>
                                        <DropdownMenuTrigger asChild>
                                          <Button
                                            variant="ghost"
                                            className="h-8 w-8 p-0"
                                          >
                                            <span className="sr-only">
                                              Open menu
                                            </span>
                                            <MoreVertical className="h-4 w-4" />
                                          </Button>
                                        </DropdownMenuTrigger>
                                        <DropdownMenuContent align="end">
                                          <DropdownMenuLabel>
                                            Actions
                                          </DropdownMenuLabel>
                                          <DropdownMenuItem
                                            className="text-red-600"
                                            onClick={() =>
                                              handleDeleteTask(subtask.id)
                                            }
                                          >
                                            <Trash2 className="mr-2 h-4 w-4" />
                                            Delete
                                          </DropdownMenuItem>
                                        </DropdownMenuContent>
                                      </DropdownMenu>
                                    </TableCell>
                                  </TableRow>
                                );
                              })}
                          </React.Fragment>
                        );
                      })}
                    </>
                  )}
                </TableBody>
              </Table>
            </div>

            {/* Pagination controls with stopPropagation to prevent task opening */}
            <div
              className="flex items-center justify-between p-4"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center space-x-2">
                {!isCategorizedView && (
                  <div className="relative z-10">
                    <Select
                      value={rowsPerPage.toString()}
                      onValueChange={(value) => {
                        handleRowsPerPageChange(Number(value));
                      }}
                    >
                      <SelectTrigger
                        className="w-[100px] h-8"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <SelectValue placeholder="Rows" />
                      </SelectTrigger>
                      <SelectContent onClick={(e) => e.stopPropagation()}>
                        <SelectItem
                          value="10"
                          onClick={(e) => e.stopPropagation()}
                        >
                          10 rows
                        </SelectItem>
                        <SelectItem
                          value="20"
                          onClick={(e) => e.stopPropagation()}
                        >
                          20 rows
                        </SelectItem>
                        <SelectItem
                          value="50"
                          onClick={(e) => e.stopPropagation()}
                        >
                          50 rows
                        </SelectItem>
                        <SelectItem
                          value="100"
                          onClick={(e) => e.stopPropagation()}
                        >
                          100 rows
                        </SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                )}
                <div className="text-sm text-muted-foreground">
                  {isCategorizedView ? (
                    <>
                      <strong>{getTotalTasksCount()}</strong> total tasks across{" "}
                      <strong>{Object.keys(props.categoryCounts || {}).length}</strong> categories
                    </>
                  ) : paginationInfo ? (
                    <>
                      Showing <strong>{paginationInfo.from}</strong> to{" "}
                      <strong>{paginationInfo.to}</strong> of{" "}
                      <strong>{paginationInfo.total}</strong> tasks
                    </>
                  ) : (
                    <>
                      Showing <strong>{getVisibleTasksCount()}</strong> of{" "}
                      <strong>{getTotalTasksCount()}</strong> tasks
                    </>
                  )}
                </div>
              </div>
              {!isCategorizedView && (
                <div className="flex items-center space-x-2">
                <Button
                  variant="outline"
                  size="icon"
                  onClick={(e) => {
                    e.stopPropagation();
                    if (paginationInfo && paginationInfo.currentPage > 1) {
                      handlePageChange(paginationInfo.currentPage - 1);
                    }
                  }}
                  disabled={!paginationInfo || paginationInfo.currentPage <= 1}
                >
                  <ChevronLeft className="h-4 w-4" />
                </Button>
                {paginationInfo ? (
                  // Use paginator links for page numbers
                  Array.from({ length: Math.min(paginationInfo.lastPage, 10) })
                    .map((_, index) => {
                      // Show pages around current page
                      const startPage = Math.max(1, paginationInfo.currentPage - 4);
                      const pageNum = startPage + index;
                      if (pageNum > paginationInfo.lastPage) return null;
                      return (
                        <Button
                          key={pageNum}
                          variant={paginationInfo.currentPage === pageNum ? "default" : "outline"}
                          size="sm"
                          className="h-8 w-8"
                          onClick={(e) => {
                            e.stopPropagation();
                            handlePageChange(pageNum);
                          }}
                        >
                          {pageNum}
                        </Button>
                      );
                    })
                    .filter(Boolean)
                ) : (
                  // Fallback if no pagination info
                  Array.from({
                    length: Math.ceil(sortedTasks.length / rowsPerPage),
                  })
                    .map((_, index) => (
                      <Button
                        key={index}
                        variant={page === index ? "default" : "outline"}
                        size="sm"
                        className="h-8 w-8"
                        onClick={(e) => {
                          e.stopPropagation();
                          handlePageChange(index);
                        }}
                      >
                        {index + 1}
                      </Button>
                    ))
                )}
                {paginationInfo && paginationInfo.lastPage > 10 && (
                  <span className="text-sm text-muted-foreground px-2">
                    ... {paginationInfo.lastPage}
                  </span>
                )}
                <Button
                  variant="outline"
                  size="icon"
                  onClick={(e) => {
                    e.stopPropagation();
                    if (paginationInfo && paginationInfo.currentPage < paginationInfo.lastPage) {
                      handlePageChange(paginationInfo.currentPage + 1);
                    }
                  }}
                  disabled={!paginationInfo || paginationInfo.currentPage >= paginationInfo.lastPage}
                >
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
              )}
            </div>
          </div>
          )}
        </CardContent>
      </Card>

      {/* Task Form Modal */}
      <TaskFormModal
        open={isFormOpen}
        onOpenChange={(open) => {
          setIsFormOpen(open);
          // Reset isSubmitting when modal closes
          if (!open) {
            setIsSubmitting(false);
          }
        }}
        initialData={selectedTask}
        onSubmit={handleTaskFormSubmit}
        isSubmitting={isSubmitting}
        parentTasks={parentTasks}
        availableWorkflows={workflows}
        categories={categories}
        users={filteredUsers}
        canAssignTasks={permissions.can_assign_tasks || false}
        userRole={userRole}
        userDepartmentId={userDepartmentId}
        departments={departments || []}
      />

      {/* Task Detail Modal */}
      <TaskDetailModal
        open={isDetailOpen}
        onOpenChange={(open) => {
          setIsDetailOpen(open);
          // Reset isSubmitting when modal closes
          if (!open) {
            setIsSubmitting(false);
            // Only update URL if modal was opened from URL parameter
            if (wasOpenedFromUrl) {
              router.get('/tasks', {}, { 
                preserveState: true,
                preserveScroll: true,
                replace: true 
              });
              setWasOpenedFromUrl(false); // Reset the flag
            }
          }
        }}
        task={detailTask}
        onComplete={handleCompleteTask}
        onDefer={handleDeferTask}
        onAddNote={handleAddNote}
        onUpdateStatus={handleStatusUpdate}
        isSubmitting={isSubmitting}
        onViewParent={handleViewParentTask}
        availableWorkflows={workflows}
        users={users}
        categories={categories}
        csrf_token={csrf_token}
      />

      {/* Bulk Edit Modal */}
      <BulkEditModal
        open={isBulkEditOpen}
        onOpenChange={setIsBulkEditOpen}
        selectedTasks={selectedTasks}
        onSuccess={() => {
          // Refresh tasks after bulk edit
          refreshTasks();
          setSelectedTasks([]);
        }}
        onDelete={async () => {
          try {
            setIsSubmitting(true);
            const response = await fetch('/tasks/bulk-delete', {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                'X-CSRF-TOKEN': csrf_token,
              },
              body: JSON.stringify({
                task_ids: selectedTasks
              }),
            });

            const result = await response.json();

            if (result.success) {
              toast({
                title: "Bulk Delete Successful",
                description: result.message,
              });
              refreshTasks(false);
              setSelectedTasks([]);
              setIsBulkEditOpen(false);
            } else {
              toast({
                title: "Bulk Delete Failed",
                description: result.message,
                variant: "destructive",
              });
            }
          } catch (error) {
            console.error('Bulk delete error:', error);
            toast({
              title: "Error",
              description: "Failed to delete tasks. Please try again.",
              variant: "destructive",
            });
          } finally {
            setIsSubmitting(false);
          }
        }}
        onBulkEdit={async (updates) => {
          try {
            setIsSubmitting(true);
            const response = await fetch('/tasks/bulk-edit', {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                'X-CSRF-TOKEN': csrf_token,
              },
              body: JSON.stringify({
                task_ids: selectedTasks,
                ...updates
              }),
            });

            const result = await response.json();

            if (result.success) {
              toast({
                title: "Bulk Update Successful",
                description: result.message,
              });
              refreshTasks(false);
              setSelectedTasks([]);
              setIsBulkEditOpen(false);
            } else {
              toast({
                title: "Bulk Update Failed",
                description: result.message,
                variant: "destructive",
              });
            }
          } catch (error) {
            console.error('Bulk edit error:', error);
            toast({
              title: "Error",
              description: "Failed to update tasks. Please try again.",
              variant: "destructive",
            });
          } finally {
            setIsSubmitting(false);
          }
        }}
        categories={categories}
        isSubmitting={isSubmitting}
        canDefer={checkCanDefer()}
      />

      {/* Bulk Action Sheet */}
      <BulkActionSheet
        selectedTasks={selectedTasks}
        onBulkEdit={() => setIsBulkEditOpen(true)}
        onClearSelection={() => setSelectedTasks([])}
        onBulkComplete={async () => {
          try {
            setIsSubmitting(true);
            const response = await fetch('/tasks/bulk-complete', {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                'X-CSRF-TOKEN': csrf_token,
              },
              body: JSON.stringify({
                task_ids: selectedTasks
              }),
            });

            const result = await response.json();

            if (result.success) {
              toast({
                title: "Bulk Complete Successful",
                description: result.message,
              });
              refreshTasks(false);
              setSelectedTasks([]);
            } else {
              toast({
                title: "Bulk Complete Failed",
                description: result.message,
                variant: "destructive",
              });
            }
          } catch (error) {
            console.error('Bulk complete error:', error);
            toast({
              title: "Error",
              description: "Failed to complete tasks. Please try again.",
              variant: "destructive",
            });
          } finally {
            setIsSubmitting(false);
          }
        }}
        onBulkDelete={async () => {
          try {
            setIsSubmitting(true);
            
            // Debug: Log what we're sending
            const requestBody = { task_ids: selectedTasks };
                        
            const response = await fetch('/tasks/bulk-delete', {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                'X-CSRF-TOKEN': csrf_token,
              },
              body: JSON.stringify(requestBody),
            });

            const result = await response.json();

            if (result.success) {
              toast({
                title: "Bulk Delete Successful",
                description: result.message,
              });
              refreshTasks(false);
              setSelectedTasks([]);
            } else {
              toast({
                title: "Bulk Delete Failed",
                description: result.message,
                variant: "destructive",
              });
            }
          } catch (error) {
            console.error('Bulk delete error:', error);
            toast({
              title: "Error",
              description: "Failed to delete tasks. Please try again.",
              variant: "destructive",
            });
          } finally {
            setIsSubmitting(false);
          }
        }}
        onBulkDefer={async (deferDate) => {
          try {
            setIsSubmitting(true);
            
            // Use Inertia's router for bulk defer
            router.post('/tasks/bulk-defer', {
              task_ids: selectedTasks,
              defer_date: format(deferDate, 'yyyy-MM-dd')
            }, {
              preserveScroll: true,
              onSuccess: (page) => {
                toast({
                  title: "Bulk Defer Successful",
                  description: "Tasks have been deferred successfully.",
                });
                setSelectedTasks([]);
              },
              onError: (errors) => {
                console.error('Bulk defer error:', errors);
                toast({
                  title: "Bulk Defer Failed",
                  description: "Failed to defer tasks. Please try again.",
                  variant: "destructive",
                });
              },
              onFinish: () => {
                setIsSubmitting(false);
              }
            });
          } catch (error) {
            console.error('Bulk defer error:', error);
            toast({
              title: "Error",
              description: "Failed to defer tasks. Please try again.",
              variant: "destructive",
            });
            setIsSubmitting(false);
          }
        }}
        onBulkEmail={() => {
          // TODO: Implement bulk email
          console.log('Bulk email:', selectedTasks);
        }}
        permissions={permissions}
        isVisible={selectedTasks.length > 0}
        isAtBottom={isAtBottom}
        canDefer={checkCanDefer()}
        isSubmitting={isSubmitting}
      />
    </div>
  );
}
